import type { MessageKey } from "./de.js";

export const en: Record<MessageKey, string> = {
  "gear.search": "Search item or unit",
  "gear.sort": "Sort order",
  "gear.sort.tier": "Tier first",
  "gear.sort.power": "Module power",
  "gear.sort.enhance": "Enhancement",
  "gear.filters": "Filter inventory",
  "gear.slot": "Module slot",
  "gear.tier": "Tier",
  "gear.maker": "Manufacturer",
  "gear.owner": "Assignment",
  "gear.all": "All",
  "gear.free": "In depot",
  "gear.equipped": "Equipped",
  "gear.results": "{n} of {total} modules",
  "gear.reset": "Reset filters",
  "gear.noMatch": "No matches. Change the search or filters.",
  "gear.details": "Details & management",
  "gear.pages": "Inventory pages",
  "gear.previous": "Previous",
  "gear.next": "Next",
  "gear.filled": "{n} of 5 slots filled",
  "gear.slots": "Five equipment slots",
  "gear.empty": "Empty",
  "gear.emptySlot": "This slot is empty",
  "gear.unitSections": "Unit sections",
  "gear.profile": "Profile & upgrades",
  "gear.equipment": "Equipment",
  "base.rotate": "Rotate building",
  "base.openEvents": "Open events",
  "base.openStore": "Open store",
  "base.openVip": "Open VIP benefits",
  "ui.navigation": "Main navigation",
  "ui.galleryTitle": "Component gallery",
  "ui.galleryHint": "Actual game components with sample data. Actions here do not change an account, resources or purchases.",
  "ui.language": "Language",
  "ui.reduced": "Reduce motion",
  "ui.tokens": "Colours and design",
  "ui.spacing": "Spacing",
  "ui.type": "Type scale",
  "ui.radii": "Radii and shadows",
  "ui.buttons": "Buttons",
  "ui.button.default": "Default",
  "ui.button.primary": "Primary",
  "ui.button.gold": "Premium",
  "ui.button.ghost": "Quiet",
  "ui.button.danger": "Danger",
  "ui.disabled": "Disabled",
  "ui.buttonsHint": "Loading and disabled states prevent actions; keyboard focus stays visible.",
  "ui.tabsBadges": "Tabs and badges",
  "ui.ready": "Ready",
  "ui.busy": "In progress",
  "ui.locked": "Locked",
  "ui.numbersTimers": "Numbers, plurals and timers",
  "ui.one": "One activity",
  "ui.add": "+1,200",
  "ui.reset": "Reset",
  "ui.progress": "Progress and costs",
  "ui.units": "Units: tier, stars and damage",
  "ui.buildings": "Building cards",
  "ui.overlays": "Dialogs and hints",
  "ui.modal": "Modal dialog",
  "ui.longTitle": "Long profile title",
  "ui.sheet": "Inspection sheet",
  "ui.bottomSheet": "Bottom sheet modal",
  "ui.tooltip": "Open hint",
  "ui.tooltipText": "This hint appears on focus, touch and hover. Escape closes it.",
  "ui.keyboardHint": "Escape closes the dialog. Modals contain keyboard focus; inspection sheets keep the base interactive.",
  "ui.dialogText": "This window uses the same dialog component as the story viewer and base panels.",
  "ui.sampleName": "Sample name",
  "ui.sampleAction": "Sample action",
  "ui.offers": "Offers: available, sold out, unaffordable",
  "ui.feedback": "Notifications and rewards",
  "ui.sampleData": "Sample data is displayed.",
  "ui.errorExample": "Example error message: the action failed.",
  "ui.actionResult": "Sample action completed. Account data is unchanged.",
  "ui.motionNavigation": "Motion and navigation",
  "ui.playMotion": "Show reward flight",
  "ui.footer": "The gallery supports visual review. Full gameplay and real-device checks remain separate evidence.",
  "story.enlarge": "Enlarge story panel",
  "story.unavailable": "The story image could not be loaded.",
  "story.retry": "Load image again",
  "story.illustration": "The pictured squad illustrates the story; your selected formation enters the battle.",
  "story.beforeAttack": "The {n} fuel cost is charged only when you start the attack.",
  "story.skipAttack": "Skip and attack",
  "siege.hint": "Hold the base one wave at a time. Unit and wall damage carries forward; boss waves arrive at 10 and 20.",
  "siege.phase.intermission": "Between waves",
  "siege.phase.completed": "Siege survived",
  "siege.phase.defeated": "Defences breached",
  "siege.phase.abandoned": "Siege ended",
  "siege.phase.expired": "Event expired",
  "siege.survived": "{n} / {max} waves survived",
  "siege.points": "{n, plural, one {# point} other {# points}}",
  "siege.wall": "Wall",
  "siege.reserved": "This garrison remains reserved for the siege. Its home defence stays active. An emergency repair fully restores exactly one unit.",
  "siege.formationReserved": "Reserved for an active siege. Formation and garrison changes remain locked until it ends; unit details stay available.",
  "siege.return": "Return to Event Plaza",
  "siege.wrecked": "Wreck · 0% HP",
  "siege.repair": "Repair · {n} Credits",
  "siege.next": "Start wave {n}",
  "siege.replay": "Watch waves",
  "siege.abandon": "End siege",
  "siege.abandonHint": "You keep all earned points. Further waves in this event will no longer be available.",
  "siege.abandonConfirm": "End now and release the garrison",
  "siege.history": "{n, plural, one {# played wave} other {# played waves}}",
  "siege.wave": "Wave {n}",
  "siege.boss": "Boss",
  "siege.held": "Held",
  "siege.lost": "Breached",
  "siege.rewards": "Your earned points and XP are saved. Claim reached milestones in this event card; ranking rewards arrive by mail after the event.",
  "colony.collect": "Collect",
  "colony.production": "Ready to collect",
  "colony.pendingHint": "Production stays at its building until collected. Buildings and storage share the same capacity.",
  "colony.bubbleAt": "Gold highlight at {n}",
  "colony.collectAll": "Collect all",
  "colony.collectUnlock": "Unlocks at HQ 5 or VIP 3.",
  "colony.morale": "Morale",
  "colony.population": "{n, plural, one {# resident} other {# residents}}",
  "colony.moraleHint": "At 80 morale: +5% SCR and XP. Below 30: −5%. Your actions always remain available.",
  "colony.causes": "What affects morale",
  "colony.standing": "Current standing: {n}",
  "colony.cause.foundation": "Foundation",
  "colony.cause.login": "Daily visit",
  "colony.cause.win": "Victory",
  "colony.cause.loss": "Defeat",
  "colony.cause.decorations": "Placed gardens",
  "colony.cause.burning": "Active fire",
  "colony.gardens": "Colony gardens",
  "colony.gardenHint": "Each garden: +2 morale, +0.5% production and +1% XP. Occupies one free cell. Removal gives no refund.",
  "colony.gardenAdd": "Place garden · {n} SCR",
  "colony.garden": "Garden",
  "colony.removeGarden": "Remove",
  "colony.drill": "Daily training drill",
  "colony.drillHint": "20 seconds, three attempts. Tap in each green window. Participation: 1 XP module; each hit: +1. Perfect: +10 CDC.",
  "colony.drillStart": "Start drill · 20 s",
  "colony.drillClaimed": "Completed today",
  "colony.drillAttempt": "Attempt {n} / 3",
  "colony.drillNow": "Tap NOW",
  "colony.drillTap": "Wait for green",
  "colony.drillMissed": "Missed · continue",
  "colony.drillFinish": "Claim reward",
  "colony.activity": "{n, plural, one {# colony activity} other {# colony activities}}",
  "colony.beacon.upgrade": "Upgrade available",
  "colony.beacon.collect": "Collect production",
  "colony.beacon.freePull": "Free fabrication",
  "colony.beacon.repair": "Repair complete",
  "colony.beacon.drill": "Daily drill ready",
  "units.xp.bonus": "Current XP bonus: {n}% (Hangar, morale, gardens, research and protocols).",
  "store.loadFailed": "The offers could not be loaded completely. Please try again.",
  "store.retry": "Try again",
  "store.expired": "Expired",
  "units.xp.reviewHint": "Choose Training Data, review the result, then apply once. Reset clears your selection without spending.",
  "units.xp.quantity": "Number of {n} XP modules",
  "units.xp.max": "Max to current cap",
  "units.xp.reset": "Reset selection",
  "units.xp.calculating": "Calculating quantities and resulting stats…",
  "units.xp.preview": "Level {from} to {to} · {xp} XP",
  "units.xp.noSelection": "No modules selected.",
  "units.xp.banked": "{n} surplus XP stays saved for a later cap increase.",
  "units.xp.apply": "Apply reviewed batch",
  "units.xp.milestone": "Milestone reached · Level {n}",
  "units.xp.levelUp": "Level up! Level {n}",
  "units.xp.applied": "{xp} XP applied",
  "units.compare.title": "Compare equipment",
  "units.compare.action": "Compare",
  "units.compare.stat": "Stat",
  "units.compare.current": "Current",
  "units.compare.after": "After",
  "units.compare.change": "Change",
  "units.compare.replaces": "Replaces {name}; the old module returns to the depot.",
  "units.compare.empty": "Fills an empty module slot.",
  "units.compare.currentSets": "Current sets",
  "units.compare.afterSets": "Sets after equip",
  "units.compare.transfer": "Donating unit: {name}",
  "units.compare.free": "Equipping is free. Nothing changes until you confirm.",
  "units.compare.equip": "Equip reviewed module",
  "units.compare.target": "Target unit",
  "units.compare.stat.power": "Power",
  "units.compare.stat.hp": "HP",
  "units.compare.stat.atk": "ATK",
  "units.compare.stat.def": "DEF",
  "units.compare.stat.intervalTicks": "Attack interval",
  "units.compare.stat.crit": "Crit chance",
  "units.compare.stat.critDmg": "Crit damage",
  "units.compare.stat.acc": "Accuracy",
  "units.compare.stat.eva": "Evasion",
  "units.compare.stat.energyPerAttack": "Energy / attack",
  "units.compare.stat.cargo": "Cargo",
  "units.compare.effect.hpPct": "HP",
  "units.compare.effect.speedPct": "Attack speed",
  "units.compare.effect.critPct": "Crit chance",
  "units.compare.effect.auraPct": "Aura strength",
  "units.compare.effect.dmgVsShieldedPct": "Damage vs. shields",
  "base.residentsUnavailable": "The residents could not be loaded. Please reload this view.",
  "app.name": "ZEnd",
  "app.tagline": "One minute. One credit. No mercy.",
  "auth.login": "Log in",
  "auth.register": "Create account",
  "auth.name": "Name",
  "auth.password": "Password",
  "auth.password.confirm": "Repeat password",
  "auth.password.mismatch": "The passwords do not match.",
  "auth.name.hint": "3–20 characters: letters, digits, underscore",
  "auth.password.hint": "At least 8 characters",
  "auth.noAccount": "No commander yet?",
  "auth.haveAccount": "Already have an account?",
  "auth.submitting": "One moment …",
  "auth.logout": "Log out",
  "auth.logoutAll": "Log out everywhere",
  "auth.mustChange": "Please change your starting password.",
  "auth.invite": "Invite code",
  "auth.invite.hint": "This server is invitation-only: ask a player who is already in.",
  "auth.error.INVITE_REQUIRED": "You need an invite code to get in here.",
  "auth.error.INVITE_INVALID": "This invite code does not exist.",
  "auth.error.INVITE_USED": "This invite code has already been used.",
  "auth.error.NAME_TAKEN": "That name is already taken.",
  "auth.error.UNAUTHORIZED": "Name or password is wrong.",
  "auth.error.VALIDATION": "Please check your input.",
  "auth.error.generic": "That did not work. Please try again.",
  "hud.credits": "{n, plural, one {# credit} other {# credits}}",
  "hud.credits.next": "Next credit in {s}s",
  "hud.vip": "VIP {level}",
  "hud.megavip": "MegaVIP {rank}",
  "hud.day": "Day {day}",
  "nav.base": "Base",
  "nav.map": "Map",
  "nav.units": "Units",
  "nav.node": "NODE",
  "nav.store": "Store",
  "nav.events": "Events",
  "nav.chat": "Chat",
  "nav.settings": "Settings",
  "nav.alliance": "Alliance",
  "alliance.tab.overview": "Overview",
  "alliance.tab.members": "Members",
  "alliance.tab.requests": "Requests",
  "alliance.tab.log": "Log",
  "alliance.tab.contribute": "Contribute",
  "alliance.tab.buildings": "Buildings",
  "alliance.tab.shop": "Shop",
  "alliance.tab.gifts": "Gifts",
  "alliance.tab.diplomacy": "Diplomacy",
  "alliance.tab.events": "Duel",
  "alliance.tab.research": "Research",
  "alliance.research.title": "Alliance research",
  "alliance.research.hint": "Three trees, six technologies each, five levels. Officers recommend a technology; contribution lots aimed at research advance it. Every level works for every member.",
  "alliance.research.recommended": "Recommended",
  "alliance.research.recommend": "Recommend",
  "alliance.research.recommendedToast": "Recommendation set.",
  "alliance.research.none": "No technology recommended yet.",
  "alliance.research.pick": "Pick a technology.",
  "alliance.research.donate": "Donate a lot → research",
  "alliance.research.needsLevel": "from alliance level {n}",
  "alliance.research.needsBuilding": "{name} level {n}",
  "alliance.research.lock.MAX": "Complete",
  "alliance.research.lock.ALLIANCE_LEVEL": "Alliance level {n} needed",
  "alliance.research.lock.BUILDING": "Building missing",
  "alliance.research.lock.REQUIRES": "Prerequisite missing",
  "alliance.research.open": "Ready",
  "alliance.research.perLevel": "per level",
  "alliance.research.tree.development": "Development",
  "alliance.research.tree.territory": "Territory",
  "alliance.research.tree.battle": "Battle",
  "alliance.research.effect.productionPct": "Production +{n} %",
  "alliance.research.effect.buildSpeedPct": "Build speed +{n} %",
  "alliance.research.effect.repairSlots": "Repair slots +{n}",
  "alliance.research.effect.storagePct": "Storage +{n} %",
  "alliance.research.effect.researchSpeedPct": "Research speed +{n} %",
  "alliance.research.effect.scripPct": "Scrip +{n} %",
  "alliance.research.effect.cargoPct": "Cargo +{n} %",
  "alliance.research.effect.marchSlots": "Marches +{n}",
  "alliance.research.effect.assistMax": "Build helps +{n}",
  "alliance.research.effect.sensorLevel": "Scout depth +{n}",
  "alliance.research.effect.territoryRadius": "Territory +{n} tiles",
  "alliance.research.effect.marchSpeedPct": "March speed +{n} %",
  "alliance.research.effect.infectedAtkPct": "Attack vs infected +{n} %",
  "alliance.research.effect.infectedDefPct": "Defence vs infected +{n} %",
  "alliance.research.effect.raidAttempts": "Raid attempts +{n}",
  "alliance.research.effect.infectedHpPct": "HP vs infected +{n} %",
  "alliance.research.effect.arenaAttempts": "Arena attempts +{n}",
  "alliance.research.effect.duelPointsPct": "Duel points +{n} %",
  "alliance.contribute.target": "Target",
  "alliance.contribute.target.building": "Building",
  "alliance.contribute.target.research": "Research",
  "alliance.log.research": "Research: {event}",
  "alliance.duel.title": "Alliance Duel",
  "alliance.duel.hint": "Monday to Saturday: alliances are paired by power; every day has its theme, whose sources count twice, and the side with more points wins the day. The more days won takes the week (chest and alliance XP); milestones drop gifts; at the end the three top contributors fight for the title Duel Champion.",
  "alliance.duel.none": "No duel right now. The next one starts Monday 00:00; alliances that exist then are paired.",
  "alliance.duel.theme": "Day theme",
  "alliance.duel.theme.build": "Build and research",
  "alliance.duel.theme.units": "Units",
  "alliance.duel.theme.kills": "Kills",
  "alliance.duel.theme.gather": "Gathering",
  "alliance.duel.theme.all": "All-round",
  "alliance.duel.theme.arena": "Arena",
  "alliance.duel.daysWon": "Days {mine} : {theirs}",
  "alliance.duel.today": "Today",
  "alliance.duel.todayHint": "Day theme {theme} counts double · day win: a silver gift for everyone, {n} alliance coins for the day's best",
  "alliance.duel.todaySolo": "Day won from {n} points",
  "alliance.duel.dayState.won": "won",
  "alliance.duel.dayState.lost": "lost",
  "alliance.duel.dayState.draw": "draw",
  "alliance.duel.dayState.today": "running",
  "alliance.duel.dayState.open": "to come",
  "alliance.duel.champion": "Champion duel",
  "alliance.duel.championHint": "At the end of the week the three top contributors fight each other once – with their arena defence, full health, no wear. Whoever wins most is the Duel Champion.",
  "alliance.duel.championReward": "The champion gets {n} alliance coins and also:",
  "alliance.duel.finalistReward": "The other finalists get {f} alliance coins each.",
  "alliance.duel.championIs": "Duel Champion: {name}",
  "alliance.duel.championNone": "No finalists yet – whoever scores gets on the list.",
  "alliance.duel.finalistPoints": "{n} points",
  "alliance.duel.finalistWins": "{n} wins",
  "alliance.duel.fightWinner": "Winner: {name}",
  "alliance.duel.watch": "Watch the fight",
  "alliance.duel.walkover": "walkover",
  "alliance.duel.target": "Target {n} points (no opponent this week)",
  "alliance.duel.contribution": "Your contribution: {n} points",
  "alliance.duel.milestones": "Milestones",
  "alliance.duel.milestone": "{pct} % · {n} points",
  "alliance.duel.ends": "Ends in {t}",
  "alliance.duel.won": "Won · chest by mail, alliance XP credited",
  "alliance.duel.lost": "Lost · again next week",
  "alliance.duel.draw": "Draw",
  "alliance.duel.members": "{n} members",
  "alliance.duel.boardEmpty": "Nobody has scored yet — the first action leads.",
  "alliance.duel.mvpLive": "MVP if it ended now",
  "alliance.duel.mvpWeek": "MVP of the week",
  "alliance.duel.mvpNone": "Nobody yet — whoever scores first leads.",
  "alliance.duel.mvpReward": "At settlement: the frame \"Duel MVP\" and {n} alliance coins",
  "alliance.duel.mvpGot": "Frame \"Duel MVP\" in the cosmetics, {n} alliance coins by mail",
  "alliance.duel.board": "Member board",
  "alliance.duel.you": "you",
  "alliance.duel.rank": "You are rank {r} of {n} · {p} points",
  "alliance.duel.noRank": "No rank yet — your first action puts you on the board.",
  "alliance.duel.sources": "What counts today",
  "alliance.duel.sourcesHint": "Every action earns the alliance points; sources of the day theme count ×{m}.",
  "alliance.duel.perUnit": "+{p} per {per}",
  "alliance.duel.chests": "Milestone chests",
  "alliance.duel.chestOpen": "Gift is waiting · Gifts",
  "alliance.duel.chestNeed": "{n} points to go",
  "alliance.duel.history": "Last duels",
  "alliance.duel.historyEmpty": "No duel settled yet.",
  "alliance.duel.vsTarget": "against the target {n}",
  "alliance.duel.result.won": "Won",
  "alliance.duel.result.lost": "Lost",
  "alliance.duel.result.draw": "Draw",
  "alliance.log.duel": "Duel: {event}",
  "alliance.relation.neutral": "Neutral",
  "alliance.relation.allied": "Allied",
  "alliance.relation.hostile": "Hostile",
  "alliance.relation.war": "War",
  "alliance.relation.own": "Own alliance",
  "alliance.diplomacy.title": "Relations",
  "alliance.diplomacy.hint": "No war comes as a surprise: hostility is visible at once, a war needs 24 h of hostility or three raids and starts after a 12 h countdown. Allies cannot raid each other; assists between allies count double.",
  "alliance.diplomacy.none": "No relations. Search an alliance to propose an alliance or declare hostility.",
  "alliance.diplomacy.embassyHint": "Proposals (alliance, peace) need the Embassy at level 1. Hostility and war work without it.",
  "alliance.diplomacy.officersOnly": "Officers and the leader act; you see the state.",
  "alliance.diplomacy.search": "Search an alliance (name or tag)",
  "alliance.diplomacy.propose": "Propose alliance",
  "alliance.diplomacy.peace": "Propose peace",
  "alliance.diplomacy.hostile": "Declare hostility",
  "alliance.diplomacy.neutral": "Go neutral",
  "alliance.diplomacy.dissolve": "Dissolve alliance (24 h)",
  "alliance.diplomacy.war": "Declare war",
  "alliance.diplomacy.surrender": "Surrender",
  "alliance.diplomacy.breaking": "Alliance dissolving · {t} left",
  "alliance.diplomacy.countdown": "War begins in {t}",
  "alliance.diplomacy.warUntil": "War · {t} left",
  "alliance.diplomacy.score": "War score {mine} : {theirs}",
  "alliance.diplomacy.neutralAt": "Neutral possible in {t}",
  "alliance.diplomacy.gateHostile": "War possible in {t}",
  "alliance.diplomacy.gateRaids": "Raids on us (24 h): {n}/{max}",
  "alliance.diplomacy.gateOpen": "War can be declared",
  "alliance.diplomacy.proposals": "Proposals",
  "alliance.diplomacy.incoming": "Incoming",
  "alliance.diplomacy.outgoing": "Outgoing",
  "alliance.diplomacy.noProposals": "No open proposals.",
  "alliance.diplomacy.proposalAlliance": "Alliance proposal",
  "alliance.diplomacy.proposalPeace": "Peace proposal",
  "alliance.diplomacy.from": "from {name} [{tag}] · {by}",
  "alliance.diplomacy.to": "to {name} [{tag}]",
  "alliance.diplomacy.expires": "expires in {t}",
  "alliance.diplomacy.history": "History",
  "alliance.diplomacy.noHistory": "Nothing happened yet.",
  "alliance.diplomacy.event.proposal": "Proposal ({kind}) with {name} [{tag}]",
  "alliance.diplomacy.event.declined": "Proposal declined · {name} [{tag}]",
  "alliance.diplomacy.event.expired": "Proposal expired · {name} [{tag}]",
  "alliance.diplomacy.event.allied": "Alliance with {name} [{tag}] sealed",
  "alliance.diplomacy.event.breaking": "Alliance with {name} [{tag}] dissolving",
  "alliance.diplomacy.event.neutral": "Neutral again with {name} [{tag}]",
  "alliance.diplomacy.event.hostile": "Hostility with {name} [{tag}]",
  "alliance.diplomacy.event.war_declared": "War declared on {name} [{tag}]",
  "alliance.diplomacy.event.war": "War with {name} [{tag}] begun",
  "alliance.diplomacy.event.war_ended": "War with {name} [{tag}] ended · winner {winner}",
  "alliance.diplomacy.event.peace": "Peace with {name} [{tag}]",
  "alliance.diplomacy.event.surrendered": "Surrender · {name} [{tag}]",
  "alliance.diplomacy.toast.proposed": "Alliance proposed.",
  "alliance.diplomacy.toast.peaceProposed": "Peace proposed.",
  "alliance.diplomacy.toast.accepted": "Proposal accepted.",
  "alliance.diplomacy.toast.declined": "Proposal declined.",
  "alliance.diplomacy.toast.hostile": "Hostility declared.",
  "alliance.diplomacy.toast.neutral": "Relation changed.",
  "alliance.diplomacy.toast.war": "War declared · 12 h countdown.",
  "alliance.diplomacy.toast.surrendered": "Surrendered.",
  "alliance.log.diplomacy": "Diplomacy: {event} · {tag}",
  "world.outcome.allied": "Allied · no attack",
  "alliance.contribute.title": "Contribute",
  "alliance.contribute.hint": "Every lot gives the alliance experience and the focused building progress; you earn alliance coins for the shop.",
  "alliance.contribute.today": "Today {n}/{max} lots",
  "alliance.contribute.capped": "daily cap reached",
  "alliance.contribute.lotTitle": "+{xp} XP · +{coins} coins · +{progress} progress",
  "alliance.contribute.done": "Contributed: +{xp} XP, +{coins} coins.",
  "alliance.contribute.focus": "Focus building",
  "alliance.contribute.noFocus": "No focus building yet; contributions still pay XP and coins.",
  "alliance.contribute.noFocusOfficer": "Pick under Buildings where contributions go.",
  "alliance.buildings.hint": "Alliance level {n}: buildings rise to half the alliance level (rounded up), five at most.",
  "alliance.buildings.level": "Level {n}/{max}",
  "alliance.buildings.total": "now",
  "alliance.buildings.capped": "Top level for this alliance level.",
  "alliance.buildings.focused": "Focus",
  "alliance.buildings.setFocus": "Make focus",
  "alliance.buildings.focusSet": "Focus set.",
  "alliance.effect.memberCap": "+{n} members per level",
  "alliance.effect.atkPct": "+{n} % attack per level",
  "alliance.effect.defPct": "+{n} % defence per level",
  "alliance.effect.marchGatherPct": "+{n} % march and gather per level",
  "alliance.effect.shopTier": "shop tier +{n} and −2 % prices per level",
  "alliance.effect.assistMax": "+{n} build assist per level",
  "alliance.effectTotal.memberCap": "+{n} members",
  "alliance.effectTotal.atkPct": "+{n} % attack",
  "alliance.effectTotal.defPct": "+{n} % defence",
  "alliance.effectTotal.marchGatherPct": "+{n} % march and gather",
  "alliance.effectTotal.shopTier": "shop tier {n}",
  "alliance.effectTotal.assistMax": "+{n} build assist",
  "alliance.shop.hint": "Paid with alliance coins from contributions; the Trade Hub unlocks the higher shelves.",
  "alliance.gifts.title": "Gifts",
  "alliance.gifts.hint": "When a member buys a pack from Bronze up, the whole alliance gets a gift. Every member claims it once, for 24 hours.",
  "alliance.gifts.none": "No gift waiting right now.",
  "alliance.gifts.claim": "Claim",
  "alliance.gifts.claimed": "Claimed",
  "alliance.gifts.claimedToast": "Gift claimed.",
  "alliance.gifts.tier.bronze": "Bronze",
  "alliance.gifts.tier.silver": "Silver",
  "alliance.gifts.tier.gold": "Gold",
  "alliance.gifts.tier.platinum": "Platinum",
  "alliance.gifts.source.pack": "Pack from {name}",
  "alliance.gifts.source.duel": "Duel win",
  "alliance.gifts.source.milestone": "Milestone",
  "alliance.gifts.source.test": "{name}",
  "alliance.level": "Level {n}",
  "alliance.power": "Power",
  "alliance.members": "{n}/{max} members",
  "alliance.leader": "Leader:",
  "alliance.role.leader": "Leader",
  "alliance.role.officer": "Officer",
  "alliance.role.veteran": "Veteran",
  "alliance.role.member": "Member",
  "alliance.policy.open": "Open",
  "alliance.policy.approval": "Approval",
  "alliance.policy.closed": "Closed",
  "alliance.find.title": "Find an alliance",
  "alliance.find.hint": "Alliances help each other build from any distance, share a chat and later buildings, a shop and diplomacy.",
  "alliance.find.placeholder": "Name or tag …",
  "alliance.find.none": "No alliance found. Found the first one.",
  "alliance.join": "Join",
  "alliance.apply": "Apply",
  "alliance.applied": "Applied",
  "alliance.closed": "Closed",
  "alliance.full": "Full",
  "alliance.accept": "Accept",
  "alliance.decline": "Decline",
  "alliance.invitedBy": "invited by {name}",
  "alliance.create.title": "Found",
  "alliance.create.hint": "Founding takes HQ {hq} and {credits} ₵. Name 3–24 characters, tag 2–4 upper-case letters or digits.",
  "alliance.create.name": "Name",
  "alliance.create.tag": "Tag",
  "alliance.create.description": "Description (optional)",
  "alliance.create.submit": "Found alliance",
  "alliance.notice": "Notice",
  "alliance.noNotice": "No notice yet.",
  "alliance.help.title": "Build help",
  "alliance.help.hint": "Members help each other build from any distance; every help shortens the timer.",
  "alliance.help.all": "Help all",
  "alliance.help.building": "upgrade to level {n}",
  "alliance.help.helpers": "helpers",
  "alliance.help.open": "open",
  "alliance.help.done": "done",
  "alliance.help.none": "Nobody is building right now.",
  "alliance.chat": "Alliance chat",
  "alliance.contribute": "Contribute",
  "alliance.shop": "Alliance shop",
  "alliance.soon": "Arrives with the next package.",
  "alliance.membership": "Membership",
  "alliance.leave": "Leave alliance",
  "alliance.leaveConfirm": "Really leave? Joining again is blocked for an hour.",
  "alliance.disband": "Disband alliance",
  "alliance.visit": "Visit",
  "alliance.transfer": "Hand over leadership",
  "alliance.requests.officersOnly": "Officers and the leader see applications.",
  "alliance.requests.none": "No open applications.",
  "alliance.log.none": "No entries yet.",
  "alliance.log.created": "{name} founded the alliance.",
  "alliance.log.joined": "{name} joined.",
  "alliance.log.left": "{name} left the alliance.",
  "alliance.log.kicked": "{name} removed a member.",
  "alliance.log.role": "{name} changed a role: {role}.",
  "alliance.log.transferred": "{name} handed over leadership.",
  "alliance.log.invited": "{name} invited someone.",
  "alliance.log.updated": "{name} changed settings.",
  "alliance.log.ambush_won": "{name} ambushed {victim} ({loot} loot).",
  "alliance.log.ambush_lost": "{name}'s ambush failed against {victim}.",
  "alliance.log.ambushed": "{by} ambushed {name} ({loot} lost).",
  "alliance.log.ambush_repelled": "{name} fought off an ambush by {by}.",
  "alliance.log.disbanded": "{name} disbanded the alliance.",
  "alliance.visit.title": "Visiting {name}",
  "alliance.toast.created": "Alliance founded.",
  "alliance.toast.joined": "Welcome to the alliance.",
  "alliance.toast.applied": "Application sent.",
  "alliance.toast.left": "Left the alliance.",
  "alliance.toast.helped": "Helped {n}×.",
  "alliance.toast.noticeSaved": "Notice saved.",
  "alliance.toast.role": "Role changed.",
  "alliance.toast.kicked": "Member removed.",
  "alliance.toast.transferred": "Leadership handed over.",
  "alliance.toast.accepted": "Accepted.",
  "alliance.toast.declined": "Declined.",
  "alliance.toast.disbanded": "Alliance disbanded.",
  "base.error.ALREADY_MEMBER": "You are already in an alliance.",
  "base.error.COOLDOWN": "Joining is blocked for an hour after leaving.",
  "base.error.HQ_TOO_LOW": "Your HQ is too low for that.",
  "base.error.NAME_TAKEN": "Name or tag is already taken.",
  "base.error.ALLIANCE_FULL": "The alliance is full.",
  "base.error.CLOSED": "The alliance does not take members.",
  "base.error.ALREADY_APPLIED": "Your application is pending.",
  "base.error.LEADER_MUST_TRANSFER": "Hand over leadership or disband first.",
  "base.error.OFFICERS_FULL": "There are already five officers.",
  "base.error.NOT_MEMBER": "You are not in an alliance.",
  "res.RAT": "Rations",
  "res.POL": "Polymer",
  "res.ALY": "Alloy",
  "res.ENC": "Energy Cells",
  "res.SCR": "Scrip",
  "res.stamina": "Fuel",
  "fuel.title": "Fuel",
  "fuel.hint": "powers marches, expeditions and hunts. Regenerates on its own.",
  "fuel.owned": "Your stock",
  "fuel.buy": "Refill",
  "fuel.use": "Use",
  "fuel.open": "Refill fuel",
  "settings.title": "Settings",
  "settings.language": "Language",
  "settings.language.de": "Deutsch",
  "settings.language.en": "English",
  "settings.password": "Change password",
  "settings.password.current": "Current password",
  "settings.password.next": "New password",
  "settings.password.changed": "Password changed.",
  "settings.invites": "Invitations",
  "settings.invites.hint": "This server is invitation-only. Every account can mint codes; one code admits one new player and earns you nothing.",
  "settings.invites.create": "Mint a code",
  "settings.invites.open": "{open} of {max} open",
  "settings.invites.used": "used by {name}",
  "settings.invites.free": "still open",
  "settings.invites.copy": "Copy",
  "settings.invites.copied": "Code copied.",
  "settings.invites.copyFailed": "Copying failed - select the code by hand.",
  "settings.invites.none": "No codes minted yet.",
  "settings.invites.limit": "You already hold {max} open codes. Wait until one is used.",
  "settings.invites.failed": "The code could not be minted. Try again.",
  "settings.apiKeys": "API keys",
  "settings.apiKeys.hint": "For AI players and bots. The key is shown only once.",
  "settings.apiKeys.label": "Label",
  "settings.apiKeys.create": "Create key",
  "settings.apiKeys.revoke": "Revoke",
  "settings.apiKeys.new": "Your new key (copy it now):",
  "settings.apiKeys.none": "No keys yet.",
  "settings.apiKeys.scopes": "Scopes",
  "settings.sessions": "Sessions",
  "settings.sessions.current": "this one",
  "settings.graphics": "Graphics quality",
  "settings.graphics.auto": "Automatic",
  "settings.graphics.low": "Low",
  "settings.graphics.medium": "Medium",
  "settings.graphics.high": "High",
  "settings.reducedMotion": "Reduced motion",
  "settings.saved": "Saved.",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.back": "Back",
  "common.loading": "Loading …",
  "common.online": "online",
  "common.offline": "offline",
  "common.reconnecting": "Reconnecting …",
  "placeholder.pending": "This area arrives in sub-project {n}.",
  "scene.lost": "The browser paused the graphics",
  "scene.lost.hint": "This happens on phones when memory runs short. The scene rebuilds itself as soon as the browser hands it back.",
  "scene.lost.retry": "Rebuild now",
  "scene.quality": "Quality: {q}",
  "units.tab.roster": "Frames",
  "units.tab.squads": "Squads",
  "units.tab.workshop": "Workshop",
  "units.tab.repair": "Repair",
  "units.tab.armory": "Armory",
  "units.tab.collection": "Collection",
  "units.all": "All",
  "units.count": "{n, plural, one {# frame} other {# frames}}",
  "units.inSquad": "Squad {n}",
  "units.autoEquip": "Auto-equip",
  "units.autoEquip.always": "Always automatic",
  "units.autoEquip.done": "{n} modules equipped",
  "units.autoEquip.none": "Nothing to equip",
  "units.autoEquip.newUnits": "Equip new units",
  "units.autoEquip.module": "Equip on best unit",
  "settings.automation": "Automation",
  "settings.autoEquipUnits": "Auto-equip new units",
  "settings.autoEquipModules": "Auto-equip new modules",
  "units.squad.n": "Squad {n}",
  "units.squads": "Squads",
  "units.chemistry": "Chemistry",
  "units.commanderHp": "avg of fighters",
  "units.stat.interval": "Attack interval",
  "units.stat.energy": "Energy per attack",
  "units.stat.cargo": "Cargo",
  "units.wrecked": "Wrecked",
  "units.repair": "Repair",
  "units.repairing": "Repairing",
  "units.stat.power": "Power",
  "units.level": "Level",
  "units.levelCapHint": "Cap reached: star rank {stars} and HQ {hq}. Raise stars or the HQ.",
  "units.star.count": "{n} of {max} stars",
  "armory.dismantle": "Dismantle module",
  "armory.salvage": "Breaking it returns",
  "social.profile.locked": "Locked",
  "units.xpToNext": "{n} XP to the next level",
  "units.xp.none": "No Training Data in your inventory.",
  "units.star.next": "Star {n}",
  "units.star.up": "Raise star",
  "units.star.hint": "Duplicates give 10 fragments. Universal fragments of the tier fill the gap.",
  "target.rule.front": "whoever stands in front",
  "target.rule.lowest_hp": "the lowest health",
  "target.rule.lowest_hp_pct": "the most wounded",
  "target.rule.highest_atk": "the strongest attacker",
  "target.rule.highest_def": "the heaviest armour",
  "target.rule.highest_hp": "the toughest body",
  "target.rule.defender_first": "defenders first",
  "target.rule.saboteur_first": "saboteurs first",
  "target.rule.attacker_first": "attackers first",
  "target.rule.random": "at random",
  "target.rule.marked_first": "the marked target",
  "units.targeting": "Target priority",
  "units.targeting.hint": "The commander sets the first rule; after that the unit decides for itself.",
  "units.abilities": "Abilities",
  "units.abilityCap": "Hangar {n} caps",
  "units.modules": "Modules",
  "units.modules.none": "No matching module in the depot.",
  "units.unequip": "Unequip",
  "units.autofill": "Auto-fill",
  "units.setGarrison": "Set garrison",
  "units.garrison": "Garrison",
  "units.slotClear": "Clear",
  "units.noneEligible": "No frame fits this slot.",
  "units.linkLegend": "Green = same manufacturer, gold = commander, red = rivals.",
  "units.roleHint.commander": "Commanders set the targeting protocol and pulse buffs.",
  "units.roleHint.defender": "Defenders hold the line and draw fire.",
  "units.roleHint.attacker": "Attackers deal sustained damage.",
  "units.roleHint.saboteur": "Saboteurs strike the rear and debuff.",
  "units.roleHint.support": "Supports give passive auras and repair pulses.",
  "units.toast.xp": "Training Data applied.",
  "units.toast.star": "Star raised!",
  "units.viewer.error": "The unit model could not be loaded.",
  "units.viewer.actions": "See unit in action",
  "units.viewer.idle": "Stand by",
  "units.viewer.walk": "Move",
  "units.viewer.attack": "Attack",
  "units.viewer.hit": "Take a hit",
  "units.viewer.death": "Disabled",
  "units.ceremony.title": "Star upgrade complete",
  "units.ceremony.stars": "Star rank {n} reached",
  "units.ceremony.model": "Upgraded 3D model: {name}",
  "units.ceremony.modelError": "Your upgrade is saved. The 3D model could not be loaded right now.",
  "units.ceremony.power": "Power",
  "units.ceremony.levelCap": "Level cap",
  "units.ceremony.saved": "Your new star rank is saved and active immediately.",
  "units.ceremony.awakened": "Awakened! Your maximum star rank is saved and active.",
  "units.ceremony.skip": "Skip animation",
  "units.ceremony.continue": "Back to unit",
  "units.toast.ability": "Ability improved.",
  "units.toast.equip": "Module equipped.",
  "units.toast.unequip": "Module removed.",
  "units.toast.squad": "Formation saved.",
  "units.toast.autofill": "Formation filled by power.",
  "units.toast.garrison": "Garrison set.",
  "units.toast.repair": "Repair started.",
  "units.toast.repairAll": "Every repair started.",
  "units.toast.craft": "Craft started.",
  "units.toast.enhance": "Module enhanced.",
  "units.toast.dismantle": "Module dismantled.",
  "units.toast.milestone": "Reward claimed.",
  "role.commander": "Commander",
  "role.defender": "Defender",
  "role.attacker": "Attacker",
  "role.saboteur": "Saboteur",
  "role.support": "Support",
  "chassis.tank": "Tank",
  "chassis.mecha": "Mecha",
  "chassis.tripod": "Tripod",
  "chassis.android": "Android",
  "chassis.drone_carrier": "Drone Carrier",
  "slot.commander": "Commander",
  "slot.f1": "Fighter 1",
  "slot.f2": "Fighter 2",
  "slot.f3": "Fighter 3",
  "slot.f4": "Fighter 4",
  "slot.s1": "Support 1",
  "slot.s2": "Support 2",
  "doctrine.column": "Column",
  "doctrine.column.hint": "Three fighters of one chassis: +5 % HP.",
  "doctrine.balanced": "Balanced",
  "doctrine.balanced.hint": "A defender, an attacker and a saboteur: +3 % attack.",
  "doctrine.uplink": "Uplink",
  "doctrine.uplink.hint": "Two supports of one manufacturer: +10 % aura strength.",
  "module.slot.weapon": "Weapon",
  "module.slot.armor": "Armor",
  "module.slot.core": "Core",
  "module.slot.sensor": "Sensor",
  "module.slot.drive": "Drive",
  "stat.hp": "HP",
  "stat.atk": "ATK",
  "stat.def": "DEF",
  "stat.crit": "CRIT",
  "stat.critDmg": "CRIT DMG",
  "stat.acc": "ACC",
  "stat.eva": "EVA",
  "stat.energyPerAttack": "Energy",
  "stat.intervalTicks": "Speed",
  "workshop.featured": "Spotlight",
  "offer.choose": "Choose fabrication offer",
  "offer.kind.standard": "Standard",
  "offer.kind.featured": "Spotlight",
  "offer.kind.manufacturer": "Manufacturer",
  "offer.freeBadge": "FREE",
  "offer.subtitle.standard": "Frames from all five manufacturers",
  "offer.subtitle.featured": "An S frame of your choice in the spotlight: every second S is it, guaranteed after a miss",
  "offer.spotlightChoose": "Choose the week's target",
  "offer.spotlightHint": "Three S frames to choose from. The next S frame is your target at 50 %; after a miss it is guaranteed.",
  "offer.spotlightLocked": "This week the manufacturer week fixes the spotlight frame.",
  "offer.pityMeter": "S guarantee",
  "offer.pityMeterText": "{n} of {max} pulls without an S — at {max} it is certain",
  "fabrication.title": "Fabrication",
  "fabrication.subtitle": "Standard, Spotlight and Manufacturer — the rates are on the card, and so is the guarantee",
  "fabrication.freeToday": "A free fabrication waits today",
  "offer.manufacturerOnly": "Only {mfr} frames",
  "offer.changes": "Changes in",
  "offer.opening": "First standard frame · guaranteed",
  "offer.spotlight": "Current spotlight frame",
  "offer.examples": "Possible frames · Example",
  "offer.sGuarantee": "S within {n} pulls",
  "offer.aGuarantee": "A or S within {n}",
  "offer.openingShort": "First standard pull: Shieldwall Mk2 (B) guaranteed.",
  "offer.featuredGuaranteed": "Your next S-frame is guaranteed to be {name}.",
  "offer.featuredChance": "Your next S-frame: 50% chance of {name}.",
  "offer.count": "Number of frames",
  "offer.frames": "{n}× frame",
  "offer.owned": "Owned: {n}",
  "offer.vipFree": "Free pull from VIP {n} · Open VIP",
  "offer.freeSingle": "Fabricate 1 free frame",
  "offer.details": "Odds & all {n} possible frames",
  "offer.baseRates": "Base rates before guarantees and increased S odds:",
  "offer.pityExplanation": "After {n} pulls without S, S odds increase. A/S guarantees raise the rank; ranks missing from this offer upgrade to the next available higher rank.",
  "workshop.thisWeek": "This week",
  "workshop.manufacturerPromotion": "Manufacturer Week: +{pct}% stats for {mfr} units",
  "events.manufacturerInactive": "This run does not currently supply the manufacturer bonus.",
  "base.error.BANNER_CHANGED": "The banner has rotated. Review the newly featured unit; nothing was spent.",
  "workshop.pity": "Pity {n}/{max}",
  "workshop.pityA": "A guarantee at {n}",
  "workshop.guaranteed": "Spotlight guaranteed",
  "workshop.softPity": "Soft pity from {n}",
  "workshop.free": "Free pull",
  "workshop.freeUsed.day": "Free pull back tomorrow",
  "workshop.freeUsed.week": "Free pull next week",
  "workshop.token": "FAB",
  "workshop.hint": "One Credit per minute. A ten-pull costs 1 350 ₵; a day earns 1 440.",
  "workshop.openingDefender": "Your first standard fabrication guarantees Shieldwall Mk2 (B, defender). The displayed rates apply afterwards.",
  "workshop.skip": "Skip",
  "workshop.tapNext": "Tap for the next frame",
  "workshop.tapSkip": "Tap to speed up",
  "workshop.result": "{n} frames fabricated",
  "repair.full": "All repair slots occupied",
  "repair.damaged": "Damaged",
  "repair.accelerate": "Speed up",
  "repair.finish": "Finish now",
  "units.compare.allStats": "Show all stats",
  "units.compare.setDetails": "Compare manufacturer sets",
  "repair.slots": "Slots",
  "repair.speed": "Speed +{pct}%",
  "repair.bay": "Repair Bay level {n}",
  "repair.noBay": "No Repair Bay: one emergency slot.",
  "repair.all": "Repair all",
  "repair.none": "Every frame is battle-ready.",
  "armory.queue": "Crafting",
  "armory.maxTier": "up to tier {tier}",
  "armory.level": "Armory level {n}",
  "armory.locked": "Armory from HQ 10.",
  "armory.modules": "Modules",
  "armory.blueprints": "Blueprints",
  "armory.crafting": "crafting",
  "armory.noBlueprints": "No blueprints. Expedition stages 3, 6, 9 and 12 drop them.",
  "armory.noModules": "No modules yet.",
  "armory.craft": "Craft",
  "armory.blueprintHint": "{n} blueprints: 5 manufacturers × 5 slots × tiers B/A/S.",
  "craft.destination": "Finished modules automatically enter your depot.",
  "craft.toBase": "Go to base",
  "craft.idle": "Production slot available · Choose a blueprint.",
  "craft.accelerate": "Speed up",
  "craft.finish": "Finish now",
  "craft.title": "Your blueprints",
  "craft.recipeHint": "One blueprint produces one module. Costs: owned / required.",
  "craft.findPlans": "Find blueprints",
  "craft.recipes": "{n} recipes",
  "craft.tierLocked": "Crafting tier {tier} is still locked.",
  "craft.queueFull": "All production slots are occupied.",
  "craft.missing": "Materials missing · see highlighted costs.",
  "craft.ready": "Ready to craft",
  "craft.speed": "+{n}% speed",
  "craft.cost": "Owned materials and production costs",
  "craft.done": "Finished · in depot",
  "craft.equip": "Compare & equip",
  "battle.attacker": "Attackers",
  "battle.defender": "Defenders",
  "battle.taken": "Taken",
  "battle.heal": "Healing",
  "battle.victory": "Victory",
  "battle.growth": "Unit progress",
  "battle.wave": "Wave",
  "battle.wear": "HP from {before}% to {after}%",
  "battle.xpStored": "Level cap reached · {n} XP stored",
  "battle.repair.heading": "Current repair queue",
  "battle.repair.unavailable": "Repair availability could not be loaded. Refresh to get a current estimate.",
  "battle.repair.free": "Parts (ALY) and coolant (POL) are consumed on start. {used} of {slots} workshop slots occupied.",
  "battle.repair.counts": "{ready} can start now · {queued} already queued · {deployed} deployed",
  "battle.repair.duration": "This batch finishes in up to {time} at the current repair speed.",
  "battle.repair.units": "Damaged units ({n})",
  "battle.repair.ready": "Ready to queue",
  "battle.repair.queued": "In repair",
  "battle.repair.deployed": "Deployed · unavailable",
  "battle.repair.queue": "Queue {n} repairs",
  "battle.repair.refresh": "Refresh estimate",
  "battle.repair.started": "{n} repairs started. The queue has been refreshed.",
  "battle.again": "Attack again",
  "battle.again.target": "Target {target} · Squad {squad}. Current costs and availability apply.",
  "battle.modelsUnavailable": "Some battle models could not be loaded. Their position markers and report remain available.",
  "battle.pause": "Pause replay",
  "battle.resume": "Resume replay",
  "battle.reduced": "Reduced motion",
  "battle.defeat": "Defeat",
  "battle.duration": "{s} s",
  "battle.timeout": "Timeout",
  "battle.replay": "Replay",
  "battle.skip": "Skip to result",
  "battle.ultimate": "ULTIMATE",
  "battle.turret": "Turret",
  "arena.title": "Arena",
  "arena.attempts": "Attempts",
  "arena.attempt": "attempt",
  "arena.refresh": "New opponents",
  "arena.defence": "Defence: squad {n}",
  "arena.opponents": "Opponents",
  "arena.noOpponents": "No opponents in range right now. Bots and players fill the ladder.",
  "arena.attack": "Attack",
  "arena.season": "Season",
  "arena.endsIn": "ends in",
  "arena.top": "Top 10",
  "arena.log": "Battle log",
  "arena.noLog": "No arena fights yet.",
  "arena.revenge": "Revenge",
  "arena.toast.refreshed": "New opponents.",
  "arena.toast.refilled": "Attempt refilled.",
  "arena.defence.tab": "Defence",
  "arena.defence.title": "Your arena defence",
  "arena.defence.hint": "A recording: what you set up here stands at 100 % for every attacker — even when it is worn or in repair at home. Frames may stand in a fighting squad at the same time.",
  "arena.defence.recorded": "Recorded · {n} frames",
  "arena.defence.fallback": "Nothing recorded yet — your garrison stands in",
  "arena.defence.takeStrongest": "Take the strongest squad",
  "arena.defence.pick": "Pick a frame",
  "arena.defence.pickTitle": "Pick a frame",
  "arena.defence.pickHint": "Sorted by power. A frame holds one seat; pick it again and it moves.",
  "arena.defence.clear": "Clear the seat",
  "arena.defence.save": "Record the defence",
  "arena.defence.saved": "Recorded. This is how you stand in the arena now.",
  "arena.defence.noCandidates": "No frame fits this seat.",
  "arena.defence.entryEmpty": "Set up the defence",
  "arena.defence.entry": "Defence: {n} frames · {p}",
  "expedition.title": "Expeditions",
  "expedition.sweeps": "Free sweeps",
  "expedition.stamina": "{n} fuel",
  "expedition.attack": "Attack",
  "expedition.sweep": "Sweep",
  "expedition.firstClear": "First clear",
  "expedition.toast.sweep": "Sweep done.",
  "expedition.toast.chest": "Chest opened.",
  "purpose.workshop": "Fabrication",
  "purpose.openWorkshop": "Open Workshop",
  "purpose.hangar": "Hangar",
  "purpose.openRoster": "Manage frames",
  "purpose.repair": "Repair Bay",
  "purpose.openRepair": "Open repairs",
  "purpose.armory": "Armory",
  "purpose.openRecycling": "Salvage modules",
  "purpose.openArmory": "Open Armory",
  "purpose.command": "Command Center",
  "purpose.openSquads": "Open formation",
  "purpose.arena": "Arena Terminal",
  "purpose.openArena": "Open Arena",
  "purpose.expedition": "Expedition Office",
  "purpose.openExpeditions": "Open expeditions",
  "purpose.trainer": "Training Simulator",
  "trainer.pooled": "{n} XP pooled",
  "trainer.cap": "pools up to {h} h",
  "trainer.claim": "Claim {n}× Training Data",
  "trainer.toast": "Training Data claimed.",
  "common.close": "Close",
  "cat.core": "Core",
  "cat.production": "Production",
  "cat.units": "Units",
  "cat.research": "Research",
  "cat.economy": "Economy",
  "cat.defense": "Defense",
  "cat.social": "Social",
  "zone.north": "North Sector",
  "zone.east": "East Sector",
  "zone.south": "South Sector",
  "zone.west": "West Sector",
  "base.build.title": "Build",
  "nav.mission": "Mission",
  "nav.combat": "Combat",
  "next.expedition": "Mission {chapter}–{stage}",
  "next.claim.one": "Claim 1 reward",
  "next.claim.many": "Claim {n} rewards",
  "next.repair.one": "Repair 1 frame",
  "next.repair.many": "Repair {n} frames",
  "next.build": "Put up a building",
  "next.upgrade": "Upgrade {name}",
  "next.research": "Start research",
  "next.collect": "Collect {n}",
  "next.march": "Send a squad",
  "next.arena": "Into the arena",
  "next.idle": "Out to the map",
  "base.build.action": "Build",
  "base.build.locked": "From HQ {n}",
  "base.build.copies": "{built}/{max} built",
  "base.build.nextCopy": "next copy from HQ {n}",
  "base.build.ready": "Ready to build",
  "base.build.lockedGroup": "Unlocks later",
  "base.build.builtGroup": "Built",
  "base.build.new": "NEW",
  "base.build.needed": "Needed for HQ {n}",
  "base.build.noRoom": "No room left",
  "base.build.upgrade": "Upgrade",
  "base.build.filterAll": "All",
  "base.build.empty": "Nothing to build in this category.",
  "base.build.readyCount": "{n} ready",
  "base.place.valid": "Tap a free spot or drag the building. Then confirm.",
  "base.place.invalid": "No room here.",
  "base.place.confirm": "Confirm",
  "base.state.constructing": "Under construction",
  "base.state.upgrading": "Upgrading to level {n}",
  "base.speedup.title": "Speed up",
  "base.speedup.none": "No matching speedups in your inventory.",
  "base.speedup.instant": "Finish now",
  "base.speedup.credits": "Credits",
  "base.speedup.balance": "Balance: {n} ₵",
  "base.speedup.plan": "Suggested speedups",
  "base.speedup.finishes": "finishes it right away",
  "base.speedup.finishesOver": "finishes it ({t} over)",
  "base.speedup.short": "saves {t} – {rest} left afterwards (≈ {n} ₵)",
  "base.speedup.apply": "OK · use {n}",
  "base.speedup.manual": "Choose myself",
  "base.toast.speedupPlan": "{n} speedups used",
  "base.instant": "Finish now · {n} ₵",
  "base.cancel": "Cancel (full refund)",
  "base.attributes": "Stats",
  "base.upgrade": "Upgrade",
  "base.upgrade.benefits": "Upgrade benefits",
  "base.upgrade.benefitsAfter": "After completion",
  "base.upgrade.noAttributeChange": "No building bonus increases at this level.",
  "base.upgrade.nextBenefit": "Next building bonus at level {n}",
  "base.build.open": "Open",
  "base.upgrade.to": "Upgrade to level {n}",
  "base.move": "Move",
  "base.rotateInPlace": "Rotate",
  "base.show": "Show",
  "base.all": "All buildings",
  "base.shortcut.pending": "This building opens its area with the store sub-project.",
  "base.gate.title": "Cross-section",
  "base.gate.summary": "Average {avg} · required {req}",
  "base.gate.ok": "HQ upgrade open",
  "base.gate.blocked": "{n} buildings drag",
  "base.gate.maxed": "Maxed",
  "base.gate.hint": "The HQ only rises when the average level of every unlocked building meets the requirement. Unbuilt types count as level 0.",
  "base.gate.drags": "Drags",
  "base.hq.title": "Command Nexus · Level {n}",
  "base.hq.open": "Cross-section & overview",
  "base.hq.upgrade": "HQ to level {n}",
  "base.power.title": "Power",
  "base.power.ok": "Powered",
  "base.power.deficit": "Deficit · production {pct}%",
  "base.power.score": "Power score {n}",
  "base.capacity": "Capacity {n}",
  "base.queue.title": "Construction queue & builders",
  "base.queue.build": "Build queue",
  "base.queue.research": "Research queue",
  "base.queue.permanent": "{n} permanent",
  "base.queue.rental": "Rented slot {n}",
  "base.queue.chooseSlot": "Choose queue slot",
  "base.queue.builderNumber": "Builder {n}",
  "base.queue.slotNumber": "Slot {n}",
  "base.queue.period.day": "1 day",
  "base.queue.period.week": "1 week",
  "base.queue.period.month": "30 days",
  "base.zones.title": "Sectors",
  "base.zones.hint": "Sectors add building space to your Enclave. Defeat the garrison with your squad, then spend resources and time to secure the sector. Each sector shows the required HQ level.",
  "base.zones.state.locked": "Infested",
  "base.zones.state.reclaiming": "Reclaiming",
  "base.zones.state.open": "Secured",
  "base.zones.reclaim": "Reclaim",
  "base.zones.reclaiming": "Being secured …",
  "base.protocols.title": "Nexus Protocols",
  "base.protocols.hint": "Timed bonuses for the whole Enclave. Credits or Scrip.",
  "base.layouts.title": "Layouts",
  "base.layouts.hint": "Save the arrangement and restore it with one tap.",
  "base.layouts.slot": "Layout {n}",
  "base.layouts.empty": "empty",
  "base.layouts.apply": "Apply",
  "base.reason.gate": "Cross-section {avg} below {req}",
  "base.reason.hqcap": "HQ level {n} caps this building",
  "base.reason.busy": "Currently busy",
  "base.reason.max": "Maximum level reached",
  "base.reason.resources": "Not enough resources",
  "settings.autoCoverCosts": "Cover missing costs from crates automatically",
  "cover.missing": "Missing:",
  "cover.fromDepot": "From the depot:",
  "cover.enough": "enough",
  "cover.notEnough": "Not enough even with the depot, still missing",
  "cover.storage": "storage too small for the crate",
  "cover.coverAndStart": "Cover from depot & start",
  "cover.autoTag": "covers from crates",
  "cover.vipHint": "From VIP {n} the start button covers missing costs from crates on its own.",
  "base.reason.static": "Not upgradeable",
  "base.reason.queue": "Queue is full",
  "base.toast.placed": "Construction started.",
  "base.toast.moved": "Moved.",
  "base.toast.upgradeStarted": "Upgrade started.",
  "base.toast.speedup": "Sped up.",
  "base.toast.instant": "Done!",
  "base.toast.cancelled": "Cancelled, resources refunded.",
  "base.toast.rented": "Queue rented.",
  "base.toast.reclaiming": "Reclaim started.",
  "base.toast.protocol": "Protocol active.",
  "base.toast.layoutSaved": "Layout saved.",
  "base.toast.layoutApplied": "Layout applied.",
  "base.error.COPIES": "Every copy of this building is built.",
  "base.error.LOCKED": "Not unlocked yet.",
  "base.error.CELL_OCCUPIED": "That spot is occupied.",
  "base.decorate.title": "Decorate",
  "base.decorate.hint": "Decorations drop from chests, events and shops. Placing takes cells like a building; storing returns the piece.",
  "base.decorate.placed": "{n}/{max} placed",
  "base.decorate.owned": "In store",
  "base.decorate.placedList": "Placed",
  "base.decorate.nothingPlaced": "Nothing placed yet.",
  "base.decorate.place": "Place",
  "base.decorate.remove": "Store",
  "base.decorate.move": "Move",
  "base.decorate.none": "No decorations yet. Chests, events and shops drop them.",
  "base.decorate.morale": "+{n} morale",
  "base.decorate.placedToast": "Placed.",
  "base.decorate.storedToast": "Stored.",
  "base.decorate.raisedToast": "Decoration raised",
  "base.error.DECORATION_LIMIT": "The base has no room for more decoration; a higher HQ allows more.",
  "inventory.decorationHint": "Place it in the base; the piece takes cells like a building.",
  "base.error.CELL_LOCKED": "That sector is still infested.",
  "base.error.QUEUE_FULL": "The build queue is full.",
  "base.error.QUEUE_MAX": "No more queues available.",
  "base.error.CANNOT_UPGRADE": "Upgrade not possible right now.",
  "base.error.CANNOT_RESEARCH": "Research not possible right now.",
  "base.error.NOT_RUNNING": "Nothing is running here.",
  "base.error.ZONE_STATE": "This sector cannot be reclaimed right now.",
  "base.error.LAYOUT_INVALID": "That layout no longer fits the Enclave.",
  "base.error.INSUFFICIENT_RESOURCES": "Not enough resources.",
  "base.error.INSUFFICIENT_CREDITS": "Not enough Credits. Every minute earns one.",
  "base.error.INSUFFICIENT_ITEMS": "Not enough items.",
  "base.error.INSUFFICIENT_SCRIP": "Not enough Scrip.",
  "research.title": "Research",
  "research.start": "Research · level {n}",
  "research.requires": "Requires",
  "research.reason.lab": "Data Lab required",
  "research.reason.tier": "Data Lab tier {have} below {need}",
  "research.reason.requires": "Requires {name}",
  "research.board.lab": "Data Lab · level {n}",
  "research.board.labOpen": "Open lab",
  "research.board.labBuild": "Lab in build catalogue",
  "research.board.target": "Researching level {n}",
  "research.board.stages": "Research tiers · learned technologies per tier",
  "research.board.ready": "Ready to research",
  "research.board.locked": "View requirements",
  "research.board.tier": "Tier {n}",
  "research.board.pick": "Pick a technology in the tree.",
  "research.board.levelOne": "level 1 each",
  "research.board.refund": "Cancel refunds the resources spent. Used speedups are not refunded.",
  "research.reason.busy": "Being researched",
  "research.toast.started": "Research started.",
  "attr.infectedXpBonusPct": "Infected battle XP",
  "attr.blackMarketDiscountPct": "Black Market discount",
  "purpose.openTrading": "Open Black Market",
  "purpose.openMemorial": "View achievements",
  "attr.unitLevelCap": "Unit level cap",
  "attr.squadSlotsBase": "Squad slots",
  "attr.wallHp": "Wall HP",
  "attr.trapCapacity": "Trap capacity",
  "attr.vision": "Vision",
  "attr.warningSec": "Warning time",
  "attr.scoutDetail": "Scout detail",
  "attr.production.RAT": "Rations / h",
  "attr.production.POL": "Polymer / h",
  "attr.production.ALY": "Alloy / h",
  "attr.production.ENC": "Energy Cells / h",
  "attr.production.SCR": "Scrip / h",
  "attr.powerOutput": "Power output",
  "attr.survivors": "Survivors",
  "attr.capacity": "Storage capacity",
  "attr.protectedPct": "Protected",
  "attr.exchangeRatePct": "Exchange rate",
  "attr.salvageYieldPct": "Salvage yield",
  "attr.fabricationDiscountPct": "Fabrication discount (Credits)",
  "attr.freePullsDaily": "Free pulls / day",
  "attr.abilityLevelCap": "Ability level cap",
  "attr.xpDiscountPct": "XP discount",
  "attr.slots": "Slots",
  "attr.repairSpeedPct": "Repair speed",
  "attr.xpPerHour": "XP / h",
  "attr.drillXp": "Drill XP",
  "attr.squads": "Squads",
  "attr.presets": "Presets",
  "attr.marchSpeedPct": "March speed",
  "attr.maxModuleTier": "Max module tier",
  "attr.craftSpeedPct": "Craft speed",
  "attr.researchSpeedPct": "Research speed",
  "attr.maxResearchTier": "Max research tier",
  "attr.droneLevelCap": "Drone level cap",
  "attr.droneBonusPct": "Drone bonus",
  "attr.stockSlots": "Stock slots",
  "attr.caravanCapacity": "Caravan capacity",
  "attr.exchangeFeePct": "Exchange fee",
  "attr.missionCap": "Mission cap",
  "attr.missionsPerBatch": "Missions per batch",
  "attr.staminaCap": "Fuel cap",
  "attr.sweepTickets": "Free sweeps / day",
  "attr.attempts": "Attempts",
  "attr.coinBonusPct": "Coin bonus",
  "attr.efficiencyPct": "Efficiency",
  "attr.gatherSpeedPct": "Gather speed",
  "attr.cargoPct": "Cargo",
  "attr.turretHp": "Turret HP",
  "attr.turretAtk": "Turret ATK",
  "attr.shieldDiscountPct": "Shield discount",
  "attr.antiScout": "Anti-scout",
  "attr.trapDamage": "Trap damage",
  "attr.trapCapacityBonus": "Trap capacity +",
  "attr.messageLength": "Message length",
  "attr.mailCapacity": "Mail capacity",
  "attr.titleSlots": "Title slots",
  "attr.killBonusPct": "Kill bonus",
  "rsh.effect.production.RAT": "Rations production",
  "rsh.effect.production.POL": "Polymer production",
  "rsh.effect.production.ALY": "Alloy production",
  "rsh.effect.production.ENC": "Energy Cell production",
  "rsh.effect.production.SCR": "Scrip production",
  "rsh.effect.production.all": "All production",
  "rsh.effect.storage.capacity": "Storage capacity",
  "rsh.effect.storage.protected": "Protected resources",
  "rsh.effect.gather.speed": "Gathering speed",
  "rsh.effect.march.cargo": "March cargo",
  "rsh.effect.march.speed": "March speed",
  "rsh.effect.trade.discount": "Trade discount",
  "rsh.effect.salvage.yield": "Salvage yield",
  "rsh.effect.unit.defender.hp": "Defender HP",
  "rsh.effect.unit.defender.def": "Defender DEF",
  "rsh.effect.unit.attacker.atk": "Attacker ATK",
  "rsh.effect.unit.saboteur.atk": "Saboteur ATK",
  "rsh.effect.unit.saboteur.crit": "Saboteur crit",
  "rsh.effect.unit.commander.hp": "Commander HP",
  "rsh.effect.unit.support.aura": "Support aura",
  "rsh.effect.unit.chassis.tank": "Tank chassis",
  "rsh.effect.unit.chassis.mecha": "Mecha chassis",
  "rsh.effect.unit.chassis.tripod": "Tripod chassis",
  "rsh.effect.unit.all.atk": "All units ATK",
  "rsh.effect.unit.all.def": "All units DEF",
  "rsh.effect.unit.all.hp": "All units HP",
  "rsh.effect.unit.crit": "Critical hits",
  "rsh.effect.unit.energy": "Energy gain",
  "rsh.effect.commander.pulse": "Command pulse",
  "rsh.effect.build.speed": "Build speed",
  "rsh.effect.research.speed": "Research speed",
  "rsh.effect.repair.speed": "Repair speed",
  "rsh.effect.fabrication.discount": "Fabrication discount",
  "rsh.effect.march.slots": "March slots",
  "rsh.effect.stamina.cap": "Fuel cap",
  "rsh.effect.xp.gain": "XP gain",
  "rsh.effect.cdc.drop": "Data core drops",
  "rsh.effect.wall.hp": "Wall HP",
  "rsh.effect.turret.power": "Turret power",
  "rsh.effect.trap.capacity": "Trap capacity",
  "rsh.effect.trap.damage": "Trap damage",
  "rsh.effect.shield.discount": "Shield discount",
  "rsh.effect.burn.duration": "Burn duration",
  "rsh.effect.garrison.def": "Garrison DEF",
  "rsh.effect.garrison.atk": "Garrison ATK",
  "rsh.effect.scout.resist": "Scout resistance",
  "rsh.effect.warning.time": "Warning time",
  "world.kind.enclave": "Enclave",
  "world.kind.node": "Resource node",
  "world.kind.pack": "Zombie pack",
  "world.kind.elite": "Elite pack",
  "world.kind.hive": "Hive",
  "world.kind.boss": "World boss",
  "world.kind.ruin": "Ruins",
  "world.kind.mission": "Radar mission",
  "world.kind.heli": "Helicopter wreck",
  "radar.intro": "Every mission is a tile within {r} tiles of your base and stays {h} hours. Survivors and crates take a tap, digs and wrecks a squad, elites a fight.",
  "radar.full": "Storage full — finish a mission and the clock runs again.",
  "radar.nextBatch": "Next batch in",
  "radar.noRun": "No batch scheduled.",
  "radar.scanFree": "Scan (free today)",
  "radar.scan": "Scan · {n} ₵",
  "radar.empty": "Nothing on the radar — the next batch comes on its own.",
  "radar.distance": "{n} tiles",
  "radar.noTile": "no free tile — collect it here",
  "radar.expires": "vanishes in",
  "radar.kind.rescue": "Survivors",
  "radar.kind.pickup": "Supply crate",
  "radar.kind.dig": "Dig site",
  "radar.kind.drone": "Drone wreck",
  "radar.kind.elite": "Elite",
  "radar.state.walkIn": "One tap is enough — no march.",
  "radar.state.dig": "A squad marches there and digs for {n} minutes.",
  "radar.state.drone": "A squad marches there and brings the parts.",
  "radar.state.elite": "A squad marches there and fights.",
  "radar.state.marching": "Squad on its way",
  "radar.state.digging": "Squad digging ({n} minutes)",
  "radar.collect.rescue": "Rescue",
  "radar.collect.pickup": "Collect",
  "radar.go.march": "Go",
  "radar.go.fight": "Attack",
  "radar.go.show": "On the map",
  "radar.toast.collected": "Mission done.",
  "radar.toast.scanned": "A new batch is on the radar.",
  "radar.toast.heli": "A helicopter went down at {x},{y} — the alliance salvages.",
  "radar.heli.title": "Helicopter",
  "radar.heli.noAlliance": "On Saturdays a helicopter goes down next to every Dominion — you need an alliance for that.",
  "radar.heli.noDominion": "No Dominion, no helicopter: found one and a wreck falls next to it on Saturdays.",
  "radar.heli.next": "next",
  "radar.heli.waiting": "The next one goes down in",
  "radar.heli.salvaged": "{n}/{max} salvaged",
  "radar.heli.gone": "gone in",
  "radar.heli.mineDone": "You salvaged your share.",
  "radar.heli.mineMarching": "Your squad is on its way.",
  "radar.heli.mineNone": "Send a squad — 15 minutes of salvage, a share for you, the fourth pays the alliance a gift.",
  "radar.heli.share": "Share per salvage",
  "radar.heli.go": "To the wreck",
  "radar.heli.salvage": "Salvage (15 min)",
  "radar.heli.notMember": "The wreck belongs to another alliance's Dominion.",
  "radar.heli.sheetHint": "A squad salvages for {n} minutes; every member once, eight at most.",
  "world.kind.camp": "Survivor camp",
  "world.kind.vault": "Vault",
  "world.kind.citadel": "Citadel",
  "world.kind.dominion": "Dominion",
  "world.dominion.foundHere": "Found the Dominion here",
  "world.dominion.moveHere": "Move the Dominion here · {n} coins",
  "world.dominion.cooldown": "The Dominion has just moved — move again from {t}",
  "world.dominion.hq": "Alliance HQ",
  "world.dominion.ground": "Dominion grounds",
  "world.dominion.level": "Level {n}",
  "world.dominion.progress": "Donations: {n} points",
  "world.dominion.contribute": "Contribute",
  "world.dominion.toAlliance": "To the alliance",
  "world.dominion.teleport": "Teleport to the Dominion",
  "world.dominion.teleportHint": "Teleporter in the alliance shop · {n} coins",
  "world.dominion.territory": "Territory: radius {r} · gathering +{g} % per Logistics level · marches in +{m} %",
  "world.dominion.sealed": "A Dominion can only be scouted, not attacked.",
  "world.dominion.reason.OCCUPIED": "Something that stays stands here.",
  "world.dominion.reason.ENCLAVE_NEAR": "Too close to an enclave — eight tiles of clearance.",
  "world.dominion.reason.DOMINION_NEAR": "Too close to another Dominion.",
  "world.dominion.reason.GROUND_ZERO": "Not in Ground Zero.",
  "world.dominion.reason.OUTSIDE": "Outside the map.",
  "world.toast.dominionFounded": "Dominion founded.",
  "world.toast.dominionMoved": "Dominion moved.",
  "alliance.dominion.title": "Dominion",
  "alliance.dominion.hint": "The alliance's own base on the map: the HQ in the centre, the six buildings around it, a territory that helps members gather and march.",
  "alliance.dominion.none": "No Dominion yet. The leader or an officer founds it — here with one tap on the suggested spot, or on the map on a free tile.",
  "alliance.dominion.suggest": "Free block at {x},{y}",
  "alliance.dominion.noSpot": "No free block near your enclave — look on the map.",
  "alliance.dominion.found": "Found the Dominion",
  "alliance.dominion.at": "Dominion at {x},{y} · territory radius {r}",
  "alliance.dominion.show": "Show on the map",
  "alliance.dominion.teleport": "Teleport to the Dominion",
  "alliance.dominion.teleportHint": "Teleporter in the alliance shop · {n} coins · one per week",
  "alliance.dominion.buildings": "Buildings on the block",
  "alliance.tab.help": "Help",
  "alliance.rank": "Rank #{n} of {m}",
  "alliance.tile.raid": "Raid",
  "alliance.centre.sections": "Alliance sections",
  "alliance.dominion.noneShort": "No Dominion yet",
  "alliance.dominion.tap": "Tap: show on the map",
  "alliance.shop.coins": "alliance coins",
  "alliance.shop.limit": "{used}/{max} this week",
  "alliance.shop.lock.tradeHub": "Needs Trade Hub level {n}",
  "alliance.shop.lock.vip": "Needs VIP {n}",
  "alliance.shop.teleport": "Moves your enclave into the Dominion's territory — one per week.",
  "alliance.shop.soldOut": "used up",
  "alliance.shop.notEnough": "not enough coins",
  "alliance.log.dominion": "Dominion: {event}",
  "world.kind.empty": "Empty tile",
  "world.emptyTile": "Nothing here. Wasteland as far as the eye can see.",
  "world.fogged": "in the fog",
  "world.guide": "Map guide",
  "world.guide.intro": "Six things the map shows without explaining them.",
  "world.guide.shadow.title": "Why is everything in shadow?",
  "world.guide.shadow.body": "The world is divided into fields of {chunk}×{chunk} tiles. A field you have never been to sits in shadow: you see the terrain, but no living data. Who owns a tile, how much a deposit still holds, how many are gathering there, how a settlement is doing — all of it stays hidden until you have uncovered that field.",
  "world.guide.scout.title": "Lifting the shadow: Scouts and marches",
  "world.guide.scout.body": "A scout uncovers the field around its target and sends you a report ({scout} fuel). Every march also uncovers every field it travels through, so going out to gather reveals half the route along the way. Once uncovered, a field stays uncovered.",
  "world.guide.rings.title": "The fog stays, and so do the rings",
  "world.guide.rings.body": "Uncovered is not all-knowing: a tile outside your uncovered fields is marked “in the fog” and shows only what was last known. The world lies in rings around the centre; the further in, the higher the levels of deposits and enemies — and the more company you have.",
  "world.guide.tiles.title": "What a tile can hold",
  "world.guide.tiles.body": "Deposits (rations, polymer, alloy, energy cells) to gather, infected packs and hives to fight, ruins to explore, other settlements to attack or reinforce. A tile's level tells you how hard the resistance is and how large the yield.",
  "world.guide.marches.title": "Marches and slots",
  "world.guide.marches.body": "Every march holds a slot until it is home again; scouts do not count against it. A squad marches as fast as its slowest frame. What a march will do is decided when you send it — you can recall it, but not redirect it.",
  "world.guide.stamina.title": "Fuel",
  "world.guide.stamina.body": "Gathering and scouting cost no fuel; attacks do (a pack {scout}, a hive more). Fuel refills on its own, and the tins in your stores top it up.",
  "world.distance": "{n} tiles",
  "world.eta": "march {time}",
  "world.remaining": "{n} left",
  "world.occupied": "Occupied",
  "world.gather": "Gather",
  "world.attack": "Attack",
  "world.raid": "Raid",
  "world.scout": "Scout",
  "world.centre": "Centre",
  "world.bookmark": "Bookmark",
  "world.share": "Copy coordinates",
  "world.squad": "Squad {n}",
  "world.npc": "NPC",
  "world.shielded": "Shielded",
  "world.burning": "Burning",
  "world.noShield": "No shield",
  "world.desc.pack": "A pack of the undead. Victory pays Scrip, salvage tokens and, with luck, data cores.",
  "world.desc.elite": "An elite pack: tougher, but it pays in data cores.",
  "world.desc.vault": "A sealed Necropolis vault. Heavily guarded, richly stocked.",
  "world.desc.hive": "The Matriarch has {hp} % HP left. Damage persists until the daily reset.",
  "world.desc.boss": "The world boss has {hp} % HP. The window closes in",
  "world.desc.ruin": "One-time exploration: force the door or hack the lock?",
  "world.desc.camp": "Survivors trade resources for Scrip and hand out three quests a day.",
  "world.desc.citadel": "The Citadel at Ground Zero is sealed. For now.",
  "world.ruin.force": "Force",
  "world.ruin.hack": "Hack",
  "world.camp.tradesLeft": "{n} trades left today",
  "world.camp.trade": "Trade",
  "world.camp.claim": "Claim",
  "world.shield.buy": "Buy a shield (Credits)",
  "world.shield.blocked": "No shield while your attack is out.",
  "world.teleport.random": "Random teleport",
  "world.teleport.newbieHint": "During the newbie shield a targeted teleport is free (tap a tile, then choose teleport).",
  "world.extinguish": "Extinguish",
  "world.marches": "Marches",
  "world.noMarches": "No marches under way.",
  "world.recall": "Recall",
  "world.report": "Report",
  "world.find": "Find",
  "world.level": "Level",
  "world.searchPlayer": "Player name",
  "world.bookmarks": "Bookmarks",
  "world.noBookmarks": "No bookmarks yet.",
  "world.scouts": "Scout reports",
  "world.noScouts": "No scout reports yet.",
  "world.detail": "Detail {n}",
  "world.squadSize": "{n} units",
  "world.garrisonKnown": "garrison known",
  "world.scoutBlocked": "Blocked by jammers.",
  "world.boss": "World boss",
  "world.boss.titan": "Titan Necrolith",
  "world.nodeDrone.own": "Your NODE drone",
  "world.nodeDrone.toCore": "to the NODE",
  "world.nodeDrone.toBase": "homeward",
  "world.boss.health": "Boss health",
  "world.boss.loadFailed": "Model unavailable.",
  "world.zoom": "Map zoom",
  "world.zoom.near": "Close",
  "world.zoom.tactical": "Tactical",
  "world.zoom.overview": "Overview",
  "world.boss.myDamage": "Your damage: {n}",
  "world.boss.next": "Next boss in",
  "world.boss.none": "No boss announced.",
  "world.attacks": "Attacks",
  "world.noAttacks": "No attacks yet.",
  "world.revenge": "Revenge until",
  "world.filters": "Filters",
  "world.incoming": "{n} incoming",
  "world.danger.title": "Ring danger",
  "world.danger.kind.pack": "packs",
  "world.danger.kind.elite": "elite packs",
  "world.danger.kind.hive": "broods",
  "world.danger.alarm": "Monster attack from ring {ring} · {kind} level {min}–{max}",
  "world.danger.quiet": "Ring {ring} · {kind} attack here",
  "world.danger.next": "next attack in",
  "world.danger.doneToday": "none left today",
  "world.danger.counter": "losses {n}",
  "world.danger.chip": "Ring {ring} · {n}",
  "world.danger.heals": "heals in",
  "world.danger.nextLoss": "Next loss",
  "world.danger.penalty.garrison": "the garrison squad is wrecked",
  "world.danger.penalty.loot": "{pct} % of the stores looted and every squad wrecked",
  "world.danger.penalty.exile": "the enclave is thrown to the edge and {pct} % of the stores are lost",
  "world.danger.sheet.title": "Ring {ring}: the infected attack here",
  "world.danger.sheet.line": "{min}–{max} attacks a day · {kind} level {lmin}–{lmax} · shields hold",
  "world.danger.toast.incoming": "Attack from ring {ring}: {kind} level {level} on the way!",
  "world.danger.toast.held": "The garrison held the ring's attack.",
  "world.danger.toast.shielded": "The shield kept the ring's attack out.",
  "world.danger.toast.lost": "Loss {n}/{max} against the ring — see your mail.",
  "world.danger.toast.exiled": "Third loss: the enclave was thrown to the edge.",
  "world.action.gather": "Gather",
  "world.action.attack": "Attack",
  "world.action.scout": "Scout",
  "world.action.explore": "Explore",
  "world.state.marching": "Marching",
  "world.state.gathering": "Gathering",
  "world.state.returning": "Returning",
  "world.state.done": "Home",
  "world.state.waiting": "Lying in wait",
  "world.state.stationed": "On guard",
  "world.state.rallying": "Rallying",
  "world.action.rally": "Rally",
  "world.outcome.rallying": "Rally",
  "world.outcome.rallyMissed": "Too late for the rally",
  "world.outcome.rallyCancelled": "Rally called off",
  "world.outcome.rallyTargetGone": "Target gone",
  "world.outcome.hostMoved": "{name} moved away",
  "world.toast.incomingRally": "Alarm: a rally by {name} with {n} squads!",
  "world.toast.rallyOpened": "Rally opened – your alliance can join.",
  "world.toast.reinforceReleased": "{name} moved away – your guard walks home.",
  "world.rally.title": "Rally",
  "world.rally.explain": "Up to {n} squads of your alliance gather at your enclave and march together. They attack in waves: each wave meets the garrison the one before wore down. The loot is shared by cargo room.",
  "world.rally.wait": "Gathering time",
  "world.rally.start": "Open a rally",
  "world.rally.leaves": "Arrives at the target in about {time} (gathering plus march).",
  "world.rally.shielded": "The target is shielded – a rally can only go once the shield is down.",
  "alliance.rally.frames": "{n} frames",
  "alliance.defense.rallies": "Rallies",
  "alliance.defense.noRallies": "No rally is running. To open one: tap an enemy enclave on the map → Rally.",
  "alliance.defense.rallyOf": "rally, {n} squads",
  "alliance.rally.on": "Rally on {target}",
  "alliance.rally.by": "by {leader}",
  "alliance.rally.gathering": "Gathering – leaves in",
  "alliance.rally.marching": "Marching – arrives in",
  "alliance.rally.won": "Won in {waves} wave(s) · {loot} loot",
  "alliance.rally.lost": "Driven back after {waves} wave(s)",
  "alliance.rally.cancelled": "Called off",
  "alliance.rally.ready": "ready",
  "alliance.rally.onTheWay": "on the way, there in",
  "alliance.rally.squad": "Squad",
  "alliance.rally.noSquad": "No squad ready.",
  "alliance.rally.join": "Join",
  "alliance.rally.joined": "Your squad is on its way to the rally point.",
  "alliance.rally.cancel": "Call off",
  "alliance.rally.cancelledToast": "Rally called off.",
  "alliance.rally.call": "{leader} calls a rally on {target} – join on the Situation page!",
  "alliance.rally.resultWon": "Rally on {target} won!",
  "alliance.rally.resultLost": "Rally on {target} driven back.",
  "alliance.log.rally_started": "{name} calls a rally on {target}.",
  "alliance.log.rally_won": "{name}'s rally on {target} won ({squads} squads, {loot} loot).",
  "alliance.log.rally_lost": "{name}'s rally on {target} was driven back ({squads} squads).",
  "world.action.reinforce": "Reinforcement",
  "world.outcome.stationed": "On guard at {name}",
  "world.outcome.stationedOver": "Guard duty over",
  "world.outcome.reinforceBeaten": "Guard beaten",
  "world.outcome.gone": "Target gone",
  "world.outcome.heldBy": "Defeat – {name}'s reinforcement held",
  "world.toast.heldForYou": "{name}'s reinforcement drove off {attacker}.",
  "world.toast.heldAgainst": "Raid on {defender} failed – {name}'s reinforcement held.",
  "world.toast.reinforceComing": "{name} sends you reinforcements.",
  "world.toast.reinforceArrived": "{name}'s reinforcement now guards your enclave.",
  "world.toast.reinforceHeld": "Your guard at {name} drove off {attacker}!",
  "world.toast.reinforceBeaten": "{attacker} beat your guard at {name}.",
  "world.reinforce.title": "Reinforce {name}",
  "world.reinforce.explain": "Your squad guards this enclave for up to {hours} h. Every raid meets the guards first; if one holds, the raider leaves empty-handed.",
  "world.reinforce.full": "This enclave already has all the guards it takes.",
  "world.reinforce.mine": "One of your squads already guards this enclave.",
  "world.reinforce.start": "Reinforce",
  "world.reinforce.guests": "Guards in your enclave",
  "alliance.tab.defense": "Situation",
  "alliance.defense.threats": "Attacks on members",
  "alliance.defense.calm": "Nobody is attacking a member right now.",
  "alliance.defense.you": "Your enclave",
  "alliance.defense.guards": "Guards",
  "alliance.defense.stations": "The alliance's guards",
  "alliance.defense.noStations": "Nobody is on guard right now.",
  "alliance.defense.ambushes": "The alliance's ambushes",
  "alliance.defense.noAmbushes": "No ambush is laid.",
  "alliance.defense.show": "Show",
  "alliance.defense.howto": "Reinforce: tap a member's enclave on the map → Reinforce. Ambush: tap an empty tile → Lay ambush.",
  "alliance.defense.alarm": "Alliance alarm: {attacker} attacks {name}!",
  "alliance.log.reinforce_held": "{name} stood guard at {host} and drove off {attacker}.",
  "alliance.log.reinforce_beaten": "{attacker} beat {name}'s guard at {host}.",
  "world.action.mission": "Radar mission",
  "world.action.salvage": "Salvage",
  "world.action.ambush": "Ambush",
  "world.kind.ambush": "Ambush",
  "world.outcome.waiting": "Lying in wait",
  "world.outcome.ambushWon": "Ambushed {name}",
  "world.outcome.ambushLost": "Failed against {name}",
  "world.outcome.ambushExpired": "Nobody came by",
  "world.outcome.ambushed": "Ambushed by {name}",
  "world.toast.ambushWon": "Ambush: {name} caught!",
  "world.toast.ambushLost": "Ambush: {name} fought through.",
  "world.toast.ambushed": "{name} ambushed your squad at {x},{y}!",
  "world.toast.ambushRepelled": "Ambush by {name} at {x},{y} repelled.",
  "world.toast.ambushSet": "Ambush laid – the squad is on its way.",
  "world.ambush.title": "Lay an ambush",
  "world.ambush.explain": "Your squad lies in wait here for up to {hours} h. The first hostile squad passing closer than {radius} tiles is attacked.",
  "world.ambush.win": "A win takes {pct} % of its cargo; a squad on its way out turns back.",
  "world.ambush.secret": "Only you and your alliance see the ambush.",
  "world.ambush.lootOnly": "Only strike when loot is detected",
  "world.ambush.lootOnlyHint": "Empty squads walk past; the ambush keeps waiting.",
  "world.ambush.start": "Lay ambush",
  "world.ambush.here": "{name} lies in wait here",
  "world.ambush.allyLabel": "Ambush · {name}",
  "world.outcome.gathered": "{n} {res} loaded",
  "world.outcome.won": "Victory",
  "world.outcome.lost": "Defeat",
  "world.outcome.shielded": "Target was shielded",
  "world.outcome.scouted": "Report received",
  "world.outcome.explored": "Explored",
  "world.outcome.recalled": "Recalled",
  "world.outcome.noSquad": "Squad not battle-ready",
  "world.outcome.gathering": "Gathering",
  "world.toast.marchStarted": "March started.",
  "world.toast.scoutSent": "Scout drone sent.",
  "world.toast.recalled": "Squad recalled.",
  "world.toast.copied": "Coordinates copied.",
  "world.toast.bookmarked": "Bookmark saved.",
  "world.toast.traded": "Traded.",
  "world.toast.claimed": "Reward claimed.",
  "world.toast.shielded": "Shield active.",
  "world.toast.teleported": "Enclave moved.",
  "world.toast.extinguished": "Fire out.",
  "world.toast.incoming": "Alarm: {name} is attacking!",
  "world.wall": "Base wall",
  "base.surfaceLoading": "Loading base surface…",
  "base.surfaceUnavailable": "The base surface could not be loaded.",
  "world.toast.defended": "Attack by {name} repelled.",
  "world.toast.raided": "{name} raided your enclave.",
  "world.toast.raidWon": "Raid on {name} succeeded.",
  "world.toast.raidLost": "Raid on {name} failed.",
  "world.toast.boss": "World boss sighted at {x},{y}!",
  "world.toast.scoutReport": "Scout report received.",
  "social.tab.chat": "Chat",
  "social.tab.mail": "Mail",
  "social.tab.boards": "Leaderboards",
  "social.tab.neighbours": "Neighbours",
  "social.chat.global": "Global",
  "social.chat.system": "System",
  "social.chat.older": "Load older messages",
  "social.chat.empty": "Nothing said yet. Say hello.",
  "social.chat.systemReadOnly": "The system channel is read-only: announcements, boss sightings, first S-tier fabrications.",
  "social.chat.placeholder": "Message … (@name, x,y, :emote:)",
  "social.chat.mutedUntil": "You are muted.",
  "social.chat.emotes": "Emotes",
  "social.chat.emoteLocked": "Comms Tower level {n}",
  "social.chat.reply": "Reply",
  "social.chat.report": "Report",
  "social.chat.reported": "Reported.",
  "social.chat.block": "Block",
  "social.chat.blocked": "Blocked.",
  "social.chat.delete": "Delete",
  "social.chat.timeout": "10 min timeout",
  "social.chat.muted": "Muted.",
  "social.chat.battleCard": "Battle report",
  "social.chat.conversations": "Chats",
  "social.chat.retention": "The last {n} messages stay; older ones go after {h} h",
  "social.chat.dmKept": "Private · kept",
  "social.chat.new": "{n} new",
  "social.chat.unreadDivider": "{n} unread",
  "social.chat.today": "Today",
  "social.chat.yesterday": "Yesterday",
  "social.chat.back": "Back to chats",
  "social.chat.noMessages": "No messages yet",
  "social.chat.you": "You: ",
  "social.chat.unitCard": "Unit",
  "social.chat.deleted": "[deleted]",
  "social.mail.kind.all": "All",
  "social.mail.kind.system": "System",
  "social.mail.kind.battle": "Battle",
  "social.mail.kind.reward": "Reward",
  "social.mail.kind.receipt": "Receipt",
  "social.mail.kind.admin": "Admin",
  "social.mail.trash": "Trash",
  "social.mail.filter.all": "All",
  "social.mail.filter.battle": "Battles",
  "social.mail.filter.reward": "Rewards",
  "social.mail.filter.system": "System",
  "social.mail.readAll": "Mark all read",
  "social.mail.readAllDone": "All mail marked as read.",
  "social.mail.deleteRead": "Delete read",
  "social.mail.pin": "Keep",
  "social.mail.unpin": "Stop keeping",
  "social.mail.pinned": "Mail kept.",
  "social.mail.unpinned": "Mail no longer kept.",
  "social.mail.watch": "Watch",
  "social.mail.capacityHint": "When the box is full the oldest settled mail is evicted; {pinned} kept mails stay.",
  "social.mail.claimAll": "Claim all",
  "social.mail.claim": "Claim",
  "social.mail.claimed": "Reward claimed.",
  "social.mail.claimedBadge": "claimed",
  "social.mail.empty": "No mail.",
  "social.mail.delete": "Delete",
  "social.mail.restore": "Restore",
  "social.boards.live": "Live",
  "social.boards.empty": "No entries yet.",
  "social.neighbours.title": "Neighbours (15 tiles)",
  "social.neighbours.none": "Nobody nearby.",
  "social.neighbours.idle": "Not building anything.",
  "social.neighbours.assisted": "Assisted: −60 s.",
  "social.contacts.title": "Contacts",
  "social.contacts.none": "No contacts yet.",
  "social.profile.message": "Message",
  "social.profile.assist": "Assist",
  "social.profile.attack": "Attack",
  "social.profile.contactAdd": "Contact",
  "social.profile.contactRemove": "Remove contact",
  "social.profile.block": "Block",
  "social.profile.unblock": "Unblock",
  "social.profile.edit": "Edit profile",
  "social.profile.avatar": "Avatar",
  "social.profile.frame": "Frame",
  "social.profile.title": "Title",
  "social.profile.colour": "Colour",
  "social.profile.showcase": "Showcase squad",
  "social.profile.comms": "Comms Tower {n}",
  "social.profile.kills": "Kills",
  "social.profile.raids": "Raids",
  "social.profile.defences": "Defended",
  "social.profile.arena": "Arena",
  "social.profile.founded": "Founded {date}",
  "social.lastSeen.online": "online",
  "social.lastSeen.minutes": "{n} min ago",
  "social.lastSeen.hours": "{n} h ago",
  "social.lastSeen.days": "{n, plural, one {# day ago} other {# days ago}}",
  "social.lastSeen.never": "never seen",
  "settings.social": "Social",
  "settings.blocked": "Blocked players",
  "settings.blocked.none": "Nobody blocked.",
  "settings.chat.mentions": "Notify on mentions",
  "settings.chat.mail": "Notify on new mail",
  "settings.export": "Data export (JSON)",
  "settings.export.hint": "Everything the server holds about your account, as a file.",
  "settings.export.download": "Download",
  "settings.delete": "Delete account",
  "settings.delete.hint": "7-day grace period, then the account is anonymised. You can cancel until then.",
  "settings.delete.request": "Request deletion",
  "settings.delete.pending": "Deletion due on {date}",
  "settings.delete.cancel": "Cancel deletion",
  "settings.delete.confirm": "Really delete the account? After 7 days it is gone.",
  "settings.ledger": "Credit ledger",
  "settings.ledger.hint": "Every entry, nothing disappears.",
  "settings.ledger.more": "More",
  "common.claim": "Claim",
  "store.credits": "Credits",
  "store.vipPoints": "VIP points",
  "store.hours": "≈ {n} h of play",
  "store.minutes": "≈ {n} min of play",
  "store.chainStep": "Chain offer · step {n}",
  "store.limit": "{used}/{max} bought",
  "store.worth": "Worth ≈ {n} ₵",
  "store.notEnough": "Not enough Credits",
  "store.locked": "Locked",
  "store.soldOut": "Sold out",
  "store.doubleHint": "First purchase this month: double contents!",
  "store.confirmHint": "You get right away:",
  "store.balance": "Balance: {n} ₵",
  "store.fx.title": "Delivered!",
  "store.fx.double": "Delivered twice!",
  "store.fx.next": "Step {n} unlocked: {price} ₵",
  "store.toast.vip": "+{n} VIP points",
  "store.push.flash": "Flash sale in the store!",
  "store.push.offer": "Chain offer step {step}: {price} ₵",
  "store.push.vip": "VIP {level} reached!",
  "store.push.megavip": "MegaVIP rank {rank}!",
  "store.push.doubles": "New month: every pack double is back.",
  "store.section.chains": "Your chain offers",
  "store.chains.hint": "Three steps, 24 hours each – every purchase unlocks the next.",
  "store.section.flash": "Flash sales",
  "store.section.featured": "Featured",
  "store.recommended.hint": "Matches your biggest bottleneck.",
  "store.section.mustbuy": "Must-buy, weekly, monthly",
  "store.tab.featured": "Featured",
  "store.tab.packs": "Packs",
  "store.tab.milestones": "HQ milestones",
  "store.tab.daily": "Daily deals",
  "store.tab.passes": "Passes",
  "store.tab.speedups": "Speedups",
  "store.tab.resources": "Resources",
  "store.tab.fabrication": "Fabrication",
  "store.tab.vip": "VIP",
  "store.tab.lucky": "Lucky",
  "store.tab.cosmetics": "Cosmetics",
  "store.tab.shops": "Shops",
  "store.packs.hint": "The first purchase of each tier every month delivers double.",
  "store.packs.refreshed": "New month – every double is armed again!",
  "store.daily.rerolled": "New daily deals",
  "store.daily.refreshFree": "Reroll (free)",
  "store.daily.refreshPaid": "Reroll ({n} ₵)",
  "store.daily.resets": "New deals in",
  "store.pass.claimed": "Claimed",
  "store.pass.days": "{n, plural, one {# day} other {# days}}",
  "store.pass.untilHq": "Until HQ 25",
  "store.pass.active": "Active",
  "store.perk.queue_build_2": "Second build queue",
  "store.perk.xp__10": "+10 % unit XP",
  "store.perk.frame": "Frame: {v}",
  "store.perk.track_premium": "Premium track with every tier reward",
  "store.perk.drip": "{n} ₵ per day",
  "store.perk.payouts": "{n} ₵ paid out at HQ levels",
  "store.perk.milestones": "{n} milestone chests",
  "store.pass.drip": "Claim day {day}",
  "store.pass.payout": "Pay out HQ {hq}",
  "store.pass.milestone": "Milestone {n}",
  "store.pass.track.free": "Free track",
  "store.pass.track.premium": "Premium track",
  "store.pass.track.premium_plus": "Premium+ with the season skin",
  "store.pass.plus": "Premium+",
  "store.pass.tier": "Tier {tier}/{max}",
  "store.pass.buyTier": "Buy tier ({n} ₵)",
  "store.pass.tierBought": "Tier bought",
  "store.pass.battle": "Battle Pass",
  "store.vip.chestClaimed": "VIP chest claimed",
  "store.vip.points": "{n} / {next} points",
  "store.vip.production": "production",
  "store.vip.build": "build speed",
  "store.vip.units": "unit stats",
  "store.vip.streak": "Login streak {n}",
  "store.vip.chest": "Daily VIP chest",
  "store.vip.chestLocked": "From VIP 1",
  "store.vip.unlocks": "Unlocked",
  "store.vip.mega": "MegaVIP",
  "store.vip.megaHint": "Beyond VIP 20 it never ends: +1 % unit stats per rank.",
  "store.vip.shop": "VIP shop",
  "store.vip.shopHint": "Discounts up to 80 %, weekly limits.",
  "store.unlock.daily_chest": "Daily VIP chest",
  "store.unlock.vip_shop": "VIP shop",
  "store.unlock.collect_all": "Collect all",
  "store.unlock.bookmarks_30": "30 bookmarks",
  "store.unlock.free_featured_weekly": "Free featured pull every week",
  "store.unlock.queue_build_2": "Second build queue",
  "store.unlock.arena_attempts__2": "+2 arena attempts",
  "store.unlock.presets_5": "5 formation presets",
  "store.unlock.unit": "Unit: {v}",
  "store.unlock.layouts_5": "5 base layouts",
  "store.unlock.queue_research_2": "Second research queue",
  "store.unlock.stamina_cap__20": "+20 fuel cap",
  "store.unlock.march_speed__10": "+10 % march speed",
  "store.unlock.queue_build_3": "Third build queue",
  "store.unlock.repair_slots__1": "+1 repair slot",
  "store.unlock.name_gold": "Golden name",
  "store.unlock.gather_speed__15": "+15 % gather speed",
  "store.unlock.skin": "Skin: {v}",
  "store.unlock.fabrication_discount_10": "10 % fabrication discount",
  "store.unlock.chest_gold_daily": "Gold chest every day",
  "store.unlock.megavip": "MegaVIP ladder",
  "store.unlock.tactical_auto_turn": "Automatic turn in the tactical sector",
  "store.unlock.skinName.commander_vip16": "VIP commander",
  "store.unlock.skinName.base_custom": "Custom base look",
  "store.lucky.wheel": "Lucky Wheel",
  "store.lucky.vault": "Fortune Vault",
  "store.lucky.discount": "Lucky Discount",
  "store.lucky.capsules": "Capsule Machine",
  "store.lucky.jackpot": "Jackpot {n} ₵",
  "store.lucky.spinFree": "Spin for free",
  "store.lucky.spin": "Spin ({n} ₵)",
  "store.lucky.odds": "Odds",
  "store.lucky.shotsBought": "{n} shots bought",
  "store.lucky.round": "Round {n}",
  "store.lucky.treasure": "Treasure round ×3",
  "store.lucky.shots": "{n} shots",
  "store.lucky.grandWithin": "Grand prize guaranteed within the first {n}",
  "store.lucky.buyShots": "{n} shots ({price} ₵)",
  "store.lucky.shoot": "Shoot {n}×",
  "store.lucky.discountHint": "Guaranteed coupons on draws {at}.",
  "store.lucky.draws": "{n} draws",
  "store.lucky.draw": "Draw ({n} ₵)",
  "store.lucky.noCoupons": "No coupons",
  "store.lucky.discountShop": "Discount shop",
  "store.lucky.capsuleHint": "Coloured capsules drop keys – valid for {n} minutes.",
  "store.lucky.chest.green": "Green chest",
  "store.lucky.chest.blue": "Blue chest",
  "store.lucky.chest.purple": "Purple chest",
  "store.lucky.chest.gold": "Gold chest",
  "store.lucky.capsuleFree": "Draw (free, {n} left)",
  "store.lucky.capsule": "Draw ({n} ₵)",
  "store.shop.bought": "Bought",
  "store.shop.refresh.daily": "Restocks daily",
  "store.shop.refresh.weekly": "Restocks weekly",
  "store.shop.refresh.event": "Per event",
  "store.cos.equipped": "Equipped",
  "store.cos.unequipped": "Unequipped",
  "store.cos.equip": "Equip",
  "store.cos.unequip": "Unequip",
  "store.cos.reward": "Reward",
  "store.cos.previewFailed": "The 3D preview could not be loaded.",
  "store.cos.bonus.production": "production",
  "store.cos.bonus.hp": "health",
  "store.cos.kind.building_skin": "Buildings",
  "store.cos.kind.unit_skin": "Units",
  "store.cos.kind.base_skin": "Base",
  "store.cos.kind.frame": "Frames",
  "store.cos.kind.name_colour": "Name colour",
  "store.cos.kind.chat_bubble": "Chat bubbles",
  "store.cos.kind.emote_pack": "Emotes",
  "store.cos.kind.march_trail": "March trails",
  "store.cos.kind.stinger": "Stingers",
  "events.tab.plaza": "Live",
  "events.tab.daily": "Daily",
  "events.tab.growth": "Growth",
  "events.tab.achievements": "Achievements",
  "events.tab.pass": "Battle Pass",
  "events.tab.calendar": "Calendar",
  "events.tab.fightotron": "Fight-o-Tron",
  "fightotron.title": "Fight-o-Tron",
  "fightotron.intro": "Every evening from 18:00 sixteen AI teams fight, one match every five minutes. Stake {min} to {max} credits, one ticket per match. The stakes are the pot: half on the match, a fifth into the quarter-finals, the rest into the final — paid at the end by mail.",
  "fightotron.pots.match": "Match pots",
  "fightotron.pots.quarter": "Quarter-final pot",
  "fightotron.pots.final": "Final pot",
  "fightotron.pots.carry": "carried",
  "fightotron.eligible.yes": "You are in — from the quarter-finals on you may keep betting.",
  "fightotron.eligible.no": "From the quarter-finals on only those with a ticket from the opening round may bet.",
  "fightotron.settledHint": "Paid out — the winnings are in your mail.",
  "fightotron.champion": "Champion: {name} · sponsor {sponsor}",
  "fightotron.sponsor": "Sponsor",
  "fightotron.sponsor.line": "Sponsor {name}: +{pct} % attack for {mfr} frames in the fight",
  "fightotron.sponsorOfTheDay": "Sponsor of the day: {name} ({team}) — +{pct} % attack for your {mfr} frames until {time}",
  "fightotron.underdog": "Underdog: {buff}",
  "fightotron.underdogShort": "Underdog",
  "fightotron.buff.atk": "ATK +{pct} %",
  "fightotron.buff.def": "DEF +{pct} %",
  "fightotron.buff.hp": "HP +{pct} %",
  "fightotron.chance": "chance {n} %",
  "fightotron.pool": "This match's pot: {n} ₵ — goes to the tickets on the winner",
  "fightotron.ticket.pending": "Won — your share comes at the end by mail",
  "fightotron.previous": "Watch yesterday ({day})",
  "fightotron.previousBadge": "Card of {day}",
  "fightotron.today": "Back to today",
  "fightotron.previousHint": "Yesterday's fights stay watchable until the new card begins.",
  "fightotron.next": "Next match in",
  "fightotron.over": "Tonight is over",
  "fightotron.round.1": "Round of sixteen",
  "fightotron.round.2": "Quarter-final",
  "fightotron.round.3": "Semi-final",
  "fightotron.round.4": "Final",
  "fightotron.tbd": "open",
  "fightotron.tbdLong": "This seat belongs to the winner of an earlier match.",
  "fightotron.level": "Level {n}",
  "fightotron.power": "Power {n}",
  "fightotron.beat": "beat",
  "fightotron.fighting": "fighting now",
  "fightotron.back": "{n} to win · {odds}",
  "fightotron.stake": "Stake",
  "fightotron.watch": "Watch the match",
  "fightotron.ticket.open": "Your ticket: {n} at {odds}",
  "fightotron.ticket.won": "Won: +{n}",
  "fightotron.ticket.lost": "Lost: {n}",
  "fightotron.toast.placed": "Ticket written.",
  "fightotron.hero.pot": "In the pot tonight",
  "fightotron.hero.potThen": "In the pot that evening",
  "fightotron.hero.players": "{n} players · {staked} ₵ staked",
  "fightotron.split.title": "{a} ₵ against {b} ₵",
  "fightotron.split.tickets": "{n} tickets",
  "fightotron.split.ticket": "1 ticket",
  "fightotron.tickets.title": "Tickets ({n})",
  "fightotron.tickets.none": "Nobody has bet on this match yet.",
  "fightotron.tickets.nobody": "Nobody",
  "fightotron.tickets.you": "you",
  "fightotron.tickets.open": "open",
  "fightotron.tickets.won": "won",
  "fightotron.tickets.lost": "lost",
  "fightotron.winners.title": "The evening's winners",
  "fightotron.winners.champion": "Champion: {name}",
  "fightotron.winners.split": "Match {match} · QF {quarter} · Final {final}",
  "fightotron.winners.none": "No ticket won anything.",
  "fightotron.winners.carry": "Nobody cracked the final pot: {n} ₵ roll into the next evening.",
  "events.tab.progress": "Progress",
  "events.rewards.ready": "{n} rewards ready",
  "events.rewards.readyOne": "1 reward ready",
  "events.rewards.claimAll": "Claim all",
  "events.rewards.claimed": "{n} rewards claimed",
  "events.hunt.live": "Hunt live",
  "events.hunt.next": "Next hunt",
  "events.hunt.targets": "{n}/{m} targets",
  "events.none": "No event is running right now – check the calendar.",
  "events.points": "{n} points",
  "events.howto": "How do I earn points?",
  "events.tokens": "{n} event tokens per 100 points",
  "events.ranking": "Ranking",
  "events.ranking.empty": "No points yet – be the first.",
  "events.items": "items",
  "events.shop": "Event shop",
  "events.toast.milestone": "Milestone claimed",
  "events.siege.title": "Siege: {n} waves",
  "events.siege.done": "{n} of {waves} waves survived",
  "events.siege.fight": "Hold the waves",
  "events.trial.title": "Floor {floor} / {floors}",
  "events.trial.hint": "{n} fuel per floor, a chest every 10.",
  "events.trial.cleared": "Tower cleared",
  "events.trial.climb": "Next floor",
  "events.relics.title": "Relic vaults",
  "events.relics.held": "Held by {name}",
  "events.relics.free": "Unclaimed",
  "events.relics.guard": "Guardian level {level}",
  "events.relics.take": "Take",
  "events.relics.mine": "Yours",
  "events.modifier.manufacturer_bonus": "Manufacturer {d}: +{v} % stats",
  "events.modifier.node_yield": "Node yield ×{v}",
  "events.modifier.salvage_double": "Salvage ×{v}",
  "events.modifier.map_fog": "Map modifier: season fog over the world map",
  "events.modifier.hunt_spawn": "Additional hunt targets on the world map",
  "events.daily.login": "Login calendar",
  "events.daily.streak": "Streak: {n} days",
  "events.daily.claimDay": "Claim day {n}",
  "events.daily.claimed": "Claimed today",
  "events.daily.day": "Day {n}",
  "events.daily.day28": "Monthly chest on the {day}th.",
  "events.daily.missions": "Daily missions {done}/{total}",
  "events.daily.dawn": "Dawn Protocol",
  "events.daily.dawnHint": "Points from intel, training, kills, arena and repairs.",
  "events.daily.intel": "Intel Hub",
  "events.daily.intelHint": "New missions every six hours, storage {n}.",
  "events.daily.intelMinutes": "{n, plural, one {# minute} other {# minutes}}",
  "events.daily.intelStart": "Start",
  "events.pass.missions": "Pass missions today",
  "events.pass.weekly": "This week",
  "events.pass.hint": "Points from daily and weekly missions; 100 points per tier.",
  "events.toast.login": "Login reward claimed",
  "events.toast.day28": "Monthly chest claimed",
  "events.toast.intelStarted": "Mission started",
  "events.toast.intelClaimed": "Mission completed",
  "events.toast.growth": "Mission claimed",
  "events.toast.chapter": "Chapter chest claimed",
  "events.toast.achievement": "Achievement claimed",
  "events.growth.chapter": "Chapter {n}: {name}",
  "events.growth.chest": "Chapter chest",
  "events.growth.done": "Every chapter is complete.",
  "events.growth.go": "Go",
  "events.ach.building": "Building",
  "events.ach.units": "Units",
  "events.ach.combat": "Combat",
  "events.ach.world": "World",
  "events.ach.store": "Store",
  "events.ach.social": "Social",
  "events.ach.titles": "{n} titles unlocked",
  "events.ach.title": "Title: {title}",
  "events.ach.frame": "Frame: {frame}",
  "events.calendar.upcoming": "Coming up",
  "events.calendar.none": "Nothing scheduled.",
  "events.push.start": "An event has started!",
  "events.push.phase": "New event phase",
  "events.push.end": "An event ended – rewards arrive by mail.",
  "events.push.newDay": "New day: missions and chests are back.",
  "events.push.milestone": "Milestone reached!",
  "events.push.mission": "Mission done (+{n})",
  "events.push.drip": "Your pass has a daily reward waiting.",
  "events.today": "Today",
  "events.source.build_level": "Building levels",
  "events.source.research_level": "Researches",
  "events.source.unit_level": "Unit levels",
  "events.source.workshop_pull": "Fabrications",
  "events.source.pack_kill": "Packs killed",
  "events.source.elite_kill": "Elites killed",
  "events.source.hunt_kill": "Hunt kills",
  "events.source.hive_damage": "Hive damage",
  "events.source.boss_damage": "Boss damage",
  "events.source.gather": "Resources gathered",
  "events.source.stamina_spent": "Fuel spent",
  "events.source.credits_spent": "Credits spent",
  "events.source.raid_win": "Raids won",
  "events.source.defence": "Defences",
  "events.source.assist": "Neighbours assisted",
  "events.source.chat": "Chat messages",
  "events.source.arena_win": "Arena wins",
  "events.source.arena_fight": "Arena fights",
  "events.source.expedition_clear": "Expedition stages",
  "events.source.siege_wave": "Siege waves",
  "events.source.login": "Logins",
  "events.source.mail_claim": "Mail claimed",
  "events.source.repair": "Repairs",
  "events.source.mission_daily": "Daily missions",
  "events.source.store_buy": "Purchases",
  "events.source.intel": "Intel missions",
  "events.source.ruin": "Ruins explored",
  "events.source.relic_hold": "Relic hours",
  "settings.notifications": "Notifications",
  "settings.audio": "Sound and music",
  "settings.audio.hint": "Set music and effects separately. Construction, production and marches continue on the server even when the tab is closed.",
  "settings.audio.background": "Keep music playing in the background",
  "settings.audio.background.hint": "After your first click, music continues when switching tabs. Effects pause. Signing out or closing the page stops audio.",
  "settings.announcements": "Show global announcements",
  "settings.announcements.hint": "GM messages, rare finds and world boss sightings in the ticker. They remain available in system chat.",
  "social.gm": "GM",
  "social.gm.title": "Game Master · verified server account",
  "social.ticker.label": "World broadcast",
  "social.ticker.open": "Read announcement in system chat",
  "social.ticker.next": "Next announcement",
  "social.ticker.close": "Dismiss this announcement",
  "settings.audio.sound": "Sound effects and ambience",
  "settings.audio.music": "Music",
  "settings.audio.mute": "Mute",
  "settings.audio.unmute": "Unmute",
  "settings.audio.mute.sound": "Mute sound effects",
  "settings.audio.mute.music": "Mute music",
  "settings.audio.preview": "Play sound preview",
  "settings.notifications.hint": "Categories switched off are dropped on the server already.",
  "settings.notif.build": "Build finished",
  "settings.notif.research": "Research finished",
  "settings.notif.repair": "Repair done",
  "settings.notif.attack": "Attacks",
  "settings.notif.march": "Marches",
  "settings.notif.event": "Events and missions",
  "settings.notif.chest": "Chests ready",
  "settings.notif.offer": "Offers",
  "settings.notif.vip": "VIP",
  "world.assist": "Assist",
  "world.assisted": "Neighbour assisted",
  "store.cos.previewName": "Colony Keeper",
  "store.cos.previewMessage": "Welcome home, commander.",
  "store.cos.playSound": "Play sound",
  "store.cos.stopSound": "Stop sound",
  "store.cos.soundMuted": "Sound muted",
  "store.cos.packRequired": "Equip the matching emote pack",
  "progression.gain": "Power +{n}",
  "progression.total": "Enclave power",
  "progression.power.buildings": "Buildings",
  "progression.power.research": "Research",
  "progression.power.units": "Units",
  "progression.power.modules": "Modules",
  "progression.power.drone": "Drones",
  "progression.powerHint": "Your whole collection counts. Deployment, formation and repairs do not lower this value. Modules count once.",
  "progression.chapter": "Your colony grows",
  "progression.hqLevel": "HQ Level {n}",
  "progression.subtitle": "More room for people, machines and new ideas.",
  "progression.unlocked": "Now available",
  "progression.earned": "Milestone reached",
  "progression.copy": "{name}: building slot {n}",
  "progression.zone": "Reclaim {name}",
  "progression.collectAll": "Collect all production stocks together",
  "progression.buildingCap": "Upgrade buildings to level {n}",
  "progression.titleAndFragment": "New profile title and {n} Base Paint Fragment",
  "progression.continue": "Keep building",
  "progression.offers": "View HQ celebration offers",
  "progression.milestones": "HQ milestones",
  "progression.fragments": "Base Paint Fragments {n}/{required}",
  "progression.fragmentHint": "Each milestone grants one title and one fragment, once. Exchange {n} fragments for a base paint of your choice.",
  "progression.chooseSkin": "Choose a base paint",
  "progression.redeem": "Unlock for {n} fragments",
  "progression.redeemConfirm": "Fragments are spent only when you confirm. The paint is then yours permanently.",
  "progression.skinGranted": "Base paint unlocked",
  "progression.previewSkins": "Preview paints in the store",
  "store.redesign.title": "More for your next move",
  "store.redesign.subtitle": "Discover supplies, reinforcements and extras for your Enclave.",
  "store.redesign.more": "All categories",
  "store.redesign.limited": "Available for a limited time",
  "store.redesign.spotlight": "In the spotlight",
  "store.redesign.packsTitle": "Big plans. The right supplies.",
  "store.redesign.dailyTitle": "Today's supply finds",
  "store.redesign.contents": "See everything inside",
  "store.redesign.moreRewards": "{n} more rewards",
  "store.redesign.choose": "Choose offer",
  "store.redesign.remaining": "You can buy {n} more",
  "store.redesign.ends": "Ends in",
  "store.redesign.double": "Double rewards",
  "store.redesign.standard": "For your Enclave",
  "events.redesign.title": "Your next adventure",
  "events.redesign.subtitle": "Choose an event. Discover your next goal and its rewards.",
  "events.redesign.active": "Active now",
  "events.redesign.upcoming": "Coming up",
  "events.redesign.claimable": "Ready to claim",
  "events.redesign.ended": "Ended",
  "events.redesign.details": "View event",
  "events.redesign.nextReward": "Your next reward",
  "events.redesign.allRewards": "All milestones",
  "events.redesign.ranking": "Rankings and prizes",
  "events.redesign.starts": "Starts in",
  "events.redesign.ends": "Ends in",
  "events.redesign.dayEvents": "{n} events",
  "events.redesign.agenda": "Your event agenda",
  "events.redesign.calendarHint": "Choose a day. All times use Berlin time.",
  "events.redesign.noDayEvents": "No events are scheduled for this day yet.",
  "events.redesign.overview": "Overview",
  "events.redesign.availableRewards": "{n} rewards are ready",
  "nav.inventory": "Inventory",
  "settings.gm": "Game Master",
  "settings.gm.open": "Open GM console",
  "inventory.title": "My inventory",
  "inventory.subtitle": "Your supplies and reinforcements at a glance.",
  "inventory.refresh": "Refresh inventory",
  "inventory.overview": "Stock overview",
  "inventory.availableXp": "EXP available to spend",
  "inventory.xpBoxes": "in {n} training boxes · base EXP",
  "inventory.stackCount": "different items",
  "inventory.resources": "Resource stock",
  "inventory.capacityHint": "Production storage: {n} per resource, including uncollected production. Scrip is unlimited. Unopened crates are counted separately.",
  "inventory.full": "full — production has stopped",
  "inventory.fullIn": "full in {t}",
  "inventory.noProduction": "no production",
  "inventory.protected": "Protected by the vault: {n} per resource ({pct} % of the storage) — a raid takes only 5 % of it, 20 % of the rest.",
  "inventory.unprotected": "Without a vault nothing is protected.",
  "inventory.crateRule": "Crates in the depot do not count against the storage and can be opened past it.",
  "alliance.help.help": "Help",
  "alliance.help.helped": "Helped · {n} helpers",
  "alliance.help.spent": "Already helped",
  "alliance.help.finishes": "done in",
  "alliance.help.askLabel": "A line to the alliance",
  "alliance.help.askPlaceholder": "e.g. “One tap please, back in a minute”",
  "alliance.help.ask": "Ask for help",
  "alliance.help.askSent": "Your request is in the alliance chat.",
  "alliance.help.askDone": "You already asked for this upgrade.",
  "inventory.pending": "Ready to collect: {n}",
  "inventory.sealed": "In crates: {n}",
  "inventory.items": "Items",
  "inventory.search": "Find an item",
  "inventory.categories": "Item categories",
  "inventory.category.all": "All",
  "inventory.category.xp": "EXP",
  "inventory.category.resources": "Crates",
  "inventory.category.speedups": "Speedups",
  "inventory.category.recruitment": "Recruitment",
  "inventory.category.equipment": "Equipment",
  "inventory.category.boosts": "Boosts",
  "inventory.category.tokens": "Tokens",
  "inventory.category.other": "More",
  "inventory.noMatches": "No items match this selection.",
  "inventory.empty": "Your inventory is empty. Claimed item rewards will appear here.",
  "inventory.owned": "Owned: {n}",
  "inventory.xpDetail": "{each} base EXP per box · {total} EXP available in total. Bonuses are calculated in the selected unit’s preview.",
  "inventory.crateDetail": "Contains {n} {resource} per crate. These supplies have not yet been added to your resource balance.",
  "inventory.speedupDetail": "Reduces a matching active job by {n} minutes per item.",
  "inventory.xpHint": "Choose a unit and open its EXP training. Review the cost and result before confirming.",
  "inventory.useHint": "In the next screen, choose the unit or job that should use this item.",
  "inventory.chooseUnit": "Choose a unit to train",
  "inventory.openUse": "Go to use this item",
  "inventory.storedHint": "This item remains in your inventory. Its use depends on the corresponding game feature.",
  "inventory.openResource": "View {resource} and items in your inventory",
  "world.march.snapshotUnavailable": "The composition of this older march was not recorded.",
  "world.march.returnRoute": "Return route",
  "world.march.outboundRoute": "March route",
  "world.march.modelRetry": "Reload squad models",
  "world.march.showColumn": "View squad",
  "world.stamina.regen": "+1 every {minutes} min",
  "world.stamina.next": "Next point",
  "world.stamina.full": "Stock full",
  "world.stamina.cost": "Cost: {n} fuel",
  "world.stamina.slots": "Marches: {used}/{cap}",
  "inventory.used": "Added {n} {resource}.",
  "inventory.useQuantity": "Quantity",
  "inventory.usePreview": "{qty} items provide {n} {resource}.",
  "inventory.noOverflowLoss": "There must be room for all contents. If storage is full, your items are kept.",
  "inventory.refill": "Use {n} fuel refills",
  "inventory.openCrates": "Open {n} crates",
  "base.error.OVERFORGE_LOCKED": "Going past this step needs the Overforge research (military).",
  "base.error.NOT_TRADABLE": "This does not trade at the NODE – of the gear, only rank S modules do.",
  "base.error.STORAGE_FULL": "Storage cannot hold all crate contents. Spend resources or expand storage first.",
  "base.error.STAMINA_FULL": "Your fuel stock is too full for this refill. Spend fuel first.",
  "base.autoCollector.title": "Automatic collection",
  "base.autoCollector.description": "Collects at most once per hour, including offline. VIP 3 / 6 / 9 / 12: up to 3 / 6 / 9 / 12 collections per day. Allowance resets at 00:00 ZEnd time. Storage limits still apply.",
  "base.autoCollector.quota": "Today {used} / {limit} collections · hourly",
  "repair.auto.title": "Auto-repair",
  "repair.auto.cost": "Uses normal alloys and polymers. No Credits or speedups.",
  "repair.auto.description": "Checks for damaged units every minute, including offline. Automatically fills free repair slots. Deployed units are excluded; waits when resources are insufficient. Turning off stops new orders; ongoing repairs finish normally.",
  "repair.auto.locked": "Available from VIP 4.",
  "store.unlock.auto_repair": "Optional auto-repair",
  "store.unlock.auto_collect_3": "Hourly auto-collection: 3 per day",
  "store.unlock.auto_collect_6": "Hourly auto-collection: 6 per day",
  "store.unlock.auto_collect_9": "Hourly auto-collection: 9 per day",
  "store.unlock.auto_collect_12": "Hourly auto-collection: 12 per day",
  "store.unlock.build_max": "Upgrade the base in one tap (Max)",
  "store.unlock.research_path": "Research a whole path in one step",
  "store.unlock.auto_gather_1": "One squad gathers on the map on its own every 6 hours",
  "store.unlock.auto_gather_2": "Two squads gather on the map on their own every 6 hours",
  "world.autoGather.sent": "Squad {slot} gathers on its own: {res}",
  "workshop.animation": "View individually",
  "workshop.confirmBatch": "Fabricate {n} units",
  "workshop.confirmCost": "Total price: {n} Credits. All results appear together.",
  "base.autoCollector.on": "Enabled",
  "base.autoCollector.off": "Disabled",
  "base.autoCollector.locked": "Available at VIP 3. You can still collect manually.",
  "base.autoCollector.next": "Next collection:",
  "base.autoCollector.last": "Last collection: {n} resources",
  "base.autoCollector.failed": "The setting could not be saved. Please try again.",
  "inventory.storageAndCollection": "Capacity & collection",

  "base.queue.freeBuilders": "{n, plural, one {# builder available} other {# builders available}}",
  "base.queue.occupied": "{used} of {slots} occupied",
  "base.queue.hireTitle": "Hire an additional builder",
  "base.queue.expiresIn": "Rental time left",
  "base.queue.hireCopy": "An additional slot lets one more job run at the same time. Choose the rental period.",
  "base.queue.extensionCopy": "Extend your existing rented slot by the selected period. The number of slots stays the same.",
  "base.queue.rentAction": "View rental offer",
  "base.queue.extendAction": "View extension",
  "base.queue.noRenewal": "One-time price in game Credits. No automatic renewal.",
  "base.queue.maximum": "All permanent slots are unlocked.",
  "base.queue.permanentUnlocks": "Unlock permanently",
  "base.queue.vipUnlock": "VIP {vip}: {n} permanent slots",
  "base.queue.showVip": "View VIP unlocks",
  "base.queue.explanation": "Each builder handles one construction or upgrade at a time. Open a job to inspect it or use a speedup.",
  "base.queue.expiredBusy": "A rental expired. Running jobs will finish; starting another job requires an available slot.",
  "base.queue.inspectJob": "View job · Speed up",
  "base.queue.empty": "No construction job is running.",
  "base.queue.startBuilding": "Choose a building to construct",
  "base.queue.fullHelp": "All builders are busy. Speed up a job or hire an additional builder.",
  "base.queue.confirmExtend": "Extend rented slot",
  "base.queue.confirmRent": "Rent an additional slot",
  "base.queue.confirmDescription": "{kind} · additional rental period: {period}",
  "base.queue.creditStock": "Your stock: {n} Credits",
  "base.queue.creditShortage": "You need more Credits for this offer.",
  "base.queue.checkStatus": "The request was not confirmed. Your current balance and rental time are being reloaded. Check their status before renting again.",
  "base.queue.backToStatus": "Back to rental status",
  "base.queue.pay": "Confirm for {n} Credits",
  "squad.entryTitle": "Your squad moves together",
  "squad.entryHint": "Arrange your units: four fighters in front, with a commander and two supports behind them.",
  "squad.edit": "Edit squad",
  "squad.create": "Build squad",
  "squad.steps": "1. Choose a slot · 2. Select a unit · 3. Apply selection",
  "squad.filled": "{n}/7 slots filled",
  "squad.state.checking": "Checking availability",
  "squad.state.deployed": "Deployed",
  "squad.state.reserved": "Reserved for siege",
  "squad.state.repairing": "Awaiting repair",
  "squad.state.ready": "Ready to deploy",
  "squad.state.incomplete": "Not ready yet",
  "squad.toExpeditions": "Send on an expedition",
  "squad.toArena": "Into the arena",
  "squad.auto": "Build squad automatically",
  "squad.autoHint": "Auto-fill assigns and saves your strongest free, intact units to the available roles.",
  "squad.choose": "Choose unit",
  "squad.front": "Front · 4 fighters",
  "squad.frontHint": "Defenders, attackers and saboteurs stand in front. Regular attacks target this row first.",
  "squad.rear": "Rear · commander + 2 supports",
  "squad.rearHint": "Your commander and supports stand behind the fighters. Some abilities can target them directly.",
  "squad.missingCommander": "No commander: fighters can already deploy. A commander adds leadership and synergies.",
  "squad.repairHint": "An assigned unit is being repaired. Wait for it or replace it before the next deployment.",
  "squad.availabilityFailed": "Squad availability could not be loaded. Check it again before changing your formation.",
  "squad.deployedHint": "This squad is deployed on the world map. You can inspect its units and edit it again after it returns.",
  "squad.toWorld": "Open world map",
  "squad.chemistryBonus": "Chemistry +{n}%",
  "squad.inspect": "{name}: open details",
  "squad.pickerTitle": "{slot}: choose unit",
  "squad.pickerHint": "Select a unit and apply it to this slot. Cancel keeps the formation unchanged.",
  "squad.allowed": "Available roles: {roles}",
  "squad.unitReserved": "Squad {n} is deployed or reserved",
  "squad.unassigned": "Unassigned",
  "squad.selected": "Selected",
  "squad.selection": "Your selection: {name}",
  "squad.selectionEmpty": "Your selection: leave this slot empty",
  "squad.transfer": "{name} will move from squad {from} to squad {to}, leaving the previous slot empty.",
  "squad.moveSlot": "This unit will move here from {from}, leaving its previous slot empty.",
  "squad.damagedHint": "This unit needs repair. You can save the formation; deployment still follows the repair rules.",
  "squad.changedReservation": "Availability has changed. This slot cannot be saved right now.",
  "squad.apply": "Apply selection",
  "squad.retry": "Check availability again",
  "base.queue.builders": "Your builders",
  "base.queue.fullPermanentHelp": "All builders are busy. Open a running job to inspect it or use a speedup.",
  "base.error.SQUAD_BUSY": "This squad is already deployed. Wait for its return before changing the formation.",
  "world.damage": "Damage",
  "world.revengePrepare": "Prepare revenge",
  "world.revengeStart": "Launch revenge",
  "world.revengeOnce": "One counterattack. The bonus applies at enemy contact.",
  "world.revengeTooLate": "This squad arrives after the bonus expires.",
  "world.revengeUnavailable": "Revenge has already been used or has expired.",
  "world.revengeApplied": "Attacker's revenge bonus: +{n}% damage. Used for this battle.",
  "base.error.REVENGE_EXPIRED": "Revenge has already been used or has expired.",
  "base.error.REVENGE_PENDING": "A revenge squad is already marching to this opponent.",
  "base.error.REVENGE_TARGET": "The opponent moved. Prepare revenge again from the report.",
  "base.error.REVENGE_TOO_LATE": "The revenge bonus expires before your squad arrives.",
};
